import { getBrowseById, getInnerBrowse } from "#skyward_browseList";
import { CancellationError, getCallerLocation, registerErrorDataGatherer, reportException, SkywardException } from "#skyward_error";
import { isDebug, isIosApp, mobileAccess, processViewPartHtml, processViewPartHtmlSeparately } from "#skyward_global";
import { getClientHeight, getClientWidth, getCurrentPopupId, hasHorizontalScrollBar, hasVerticalScrollBar, loadContent, notifyDOMLoad, notifyResize, registerLayoutResizeCallback, registerLoadDOMCallback, reloadScreen, removeLayoutResizeCallback, removeLoadDOMCallback, scrollbarHeight } from "#skyward_window";
import { encodeHtml } from "./skyward.base.extensions";
import debounce from "./skyward.ui.debounce";
import { registerLayer, setFocus, unregisterLayer } from "./skyward.ui.dialogFocusManager";
import { DistinctMultiSelectEELData } from "./skyward.ui.distinctMultiSelectEEL";
import { getSvg } from "./skyward.ui.svg";
import { Browse, CodeEELData, Dictionary, JQueryInput } from "./types";
import { IconType, Key as SvgKey } from "./types/skyward/svg";

const skyward = window.skyward;

/**
 * Defines the functionality of modal layers such as modal dialogs and menus
 */
export interface ModalLayer
{
    /** Is the modal layer currently closing? */
    closing: boolean;
    /** The outermost element of the modal layer */
    container: JQuery<HTMLElement>;
    /** The visible dialog element within the modal layer container */
    contents: JQuery<HTMLElement>;
    /** The element that covers the page when the modal layer is visible */
    glass: JQuery<HTMLElement>;
    /** Is the modal layer visible on the screen? */
    onscreen: boolean;
    /** Options for the modal layer. */
    options: ModalLayerOptions;
    /** The element that was active when the modal layer was created */
    originalActiveElement: HTMLElement;
    /** A numerical identifier for the modal layer */
    uniqueId: number;
    /**
     * Setup the content element for the modal layer
     *
     * @param content Element to be inserted into the modal layer
     */
    setupContent(content: JQuery<HTMLElement>): void;
    /**
     * Add the content element to the modal layer
     *
     * @param content Element to be inserted into the modal layer
     */
    addContent(content: JQuery<HTMLElement>): void;
    /**
     * A function that will be called before the dialog closes.  Return false to prevent the dialog from closing.
     */
    beforeClose?: BeforeCloseFunction;
    /**
     * Close the modal layer.  Returns if the layer was closed or not.
     *
     * @param reason Reason the modal layer was closed
     * @return The completed 'layer object', which contains references to all elements, functions, and objects needed during the lifespan of the layer
     */
    close(reason?: string): boolean;
    /** Make the modal layer visible */
    show(): void;
    reasonForClose?: string;
    _bindingPoints: JQuery<HTMLElement>[];
    _exposedElements: ExposedElementData[];
    _exposedByUnderlyingLayer: ExposedElementData[];
    studentInformationPopupData?: InformationPopupData;
}

export interface InformationPopupData extends Record<string, any>
{
    module: string;
    object: string;
    action: string;
    id: string;
    title: string;
    browseName?: string;
    browseId?: string | number;
    selectedTargetId?: string | number | null;
    alternativeSelectedTargetId?: string | number;
    doesNotRequireBrowse?: boolean;
    isStudentInformationPopup?: boolean;
    isDynamicInnerBrowsePopup?: boolean;
    isChangeLogBrowsePopup?: boolean;
    isvalidationpopup?: string;
    cachepopups?: boolean;
    acknowledgedCriticalAlerts?: boolean;
    currentTabAction?: string;
}

export interface Binding
{
    onEvents: string;
    inSelectors?: string;
    runFunction: (event: JQuery.KeyboardEventBase) => boolean | void;
    bindTo?: JQueryInput;
}

export interface ModalLayerOptions
{
    afterClose?: AfterCloseFunction;
    beforeClose?: BeforeCloseFunction;
    anchored?: boolean;
    bindings?: Binding[];
    closeOnClick?: boolean;
    closeOnEscape?: boolean;
    /**
     * When set to false, will prevent the layer from being closed by skyward.window.closePopup() unless the layer is specifically passed in
     */
    closeOnRecordSelected?: boolean;
    /**
     * Will normally match the dialog's title, but is kept separate because the title could contain private info
     */
    debugName?: null | string;
    forceClose?: boolean;
    glass?: string;
    titleTag?: string;
    loadDOMCallback?: (() => void) | ((selector: string) => void) | null | false;
    popupAriaRole?: string; // TODO, reference Aria Roles
    preserveFocus?: boolean | ((reason: string) => boolean);
    resizeCallback?: (() => void) | null;
    tailessAnchored?: boolean;
    transparent?: boolean;
    additionalContainerClasses?: string;
    initialFocusElement?: JQueryInput;
    role?: string;
    expose?: JQueryInput[];
    preventKeyBubbling?: boolean;
    resizable?: boolean
}

export interface ModalDialogSettings extends ModalLayerOptions
{
    allowNoButtons?: boolean;
    useMobileAccessStyles?: boolean;
    anchorTo?: JQueryInput | null;
    draggable?: boolean;
    id?: string | null;
    transparentGlass?: boolean;
    width?: string | number | null;
    height?: string | number | null;
    animationDuration?: number;
    /**
     * If you need to make adjustments to the content after it is loaded into the modal and not yet visible to the user.
     * If used you must resolve this yourself or content won't show.
     */
    beforeShowContentPromise?: Promise<void> | null;
    /**
     * If you have a selector for modal content that triggered a skyward.window.notifyDOMLoad,
     * set it here so that the content will dynamically resize as a result of the notifyDOMLoad.
     * @example
     * An example of this would be
     * themeselector.js, dynamicDialogContentSelector is set to the HTML Id of the theme browse. When the theme browse
     * is fully loaded as a result of a getBrowseRequest, browse.js triggers a notifyDOMLoad with the id of the browse.
     * The modal knows the browse is fully loaded and sizes appropriately. In the absence of setting this you would need
     * to give the modal a fixed height or be fine with it being a height that may not be ideal.
     */
    dynamicDialogContentSelector?: string | undefined;
    additionalClasses?: string;
    /**
     * Can be used to identify specific dialogs in automated tests
     */
    automationID?: string | undefined;
    additionalContainerClasses?: string;
    hideOverflow?: boolean;
    useSmallWidth?: boolean;
    isConfirmationStep?: boolean;
}

export interface ButtonDefinition
{
    id?: string | null;
    text: string;
    icon?: SvgKey;
    click?: string | (() => any) | ((evt: JQuery.TriggeredEvent) => any) | ((evt: JQuery.TriggeredEvent, dialog: ModalDialog) => any) | ((evt: JQuery.ClickEvent, dialog: ModalDialog) => any);
    focus?: boolean;
    data?: any;
    automationID?: string;
    additionalClasses?: string;
    additionalSvgClasses?: string;
    additionalTextClasses?: string;
    disabled?: boolean;
}

export type ButtonDefinitions = (() => (ButtonDefinition[] | Promise<ButtonDefinition[]>)) | ButtonDefinition | ButtonDefinition[];

export interface PositioningManager
{
    /** Measurements related to modal positioning and sizing. */
    measurements:
    {
        allowedContentHeight: number;
        contentOnly: number;
        containerWithoutContent: number;
        entireContainer: number;
        top: number;
        containerOuterWidth?: number;
    };
    /** Anchor offset information for positioning. */
    anchorToOffset?:
    {
        top: number;
        left: number;
        right?: number;
        bottom?: number;
    };
    /**
     * Determines if the modal is anchored to an element.
     * @returns True if anchored, false otherwise.
     */
    isAnchored(): boolean;
    /** Positions the modal contents based on anchor and available space. */
    positionModalContents(): void;
    /** Positions EEL (Enhanced Editable List) contents for anchored modals. */
    positionEELContents(): void;
}

export interface ModalDialog
{
    title: JQuery<HTMLElement>;
    content: JQuery<HTMLElement>;
    footer: JQuery<HTMLElement>;
    container: JQuery<HTMLElement>;
    closed: boolean;
    close: (reason?: CloseReason) => boolean;
    animateModalContents: () => JQuery.Promise<void>;
    animationPromise: JQuery.Promise<void>;
    dynamicContentLoadedPromise: JQuery.Promise<void>;
    bodyPromise: JQuery.Promise<void>;
    modalLayer: ModalLayer;
}

export interface ModalMenu
{
    container: JQuery<HTMLElement>;
    content: JQuery<HTMLElement>;
    close: (reason?: CloseReason) => boolean;
    closed: boolean;
    modalLayer: ModalLayer;
}

export interface MenuItemDefinition
{
    type?: "item" | "title" | "custom";
    id?: string | null;
    icon?: SvgKey | JQuery<SVGSVGElement> | null;
    text?: string;
    click?: (() => void) | ((event: JQuery.ClickEvent) => void) | null;
    data?: Dictionary<string, unknown> | null;
    attributes?: Record<string, string> | null;
    disabled?: boolean;
    selected?: boolean;
    target?: "popup";
    hasPopup?: "dialog" | "false";
    content?: JQuery | (() => JQuery) | null;
    module?: string | null;
    object?: string | null;
    screen?: string | null;
}

export interface ModalMenuSettings
{
    beforeClose?: (() => void) | null;
    afterClose?: (() => void) | null;
    debugName?: string | null;
}

export interface PositioningOptions
{
    minRowsToShow?: number;
    minimumBrowseWidth: number;
    minContentHeight: number;
    maintainRelativePosition: boolean;
    getHeaderRow: () => JQuery<HTMLElement>;
    getRows: () => JQuery<HTMLElement>;
}

type ModalContents = JQueryInput | JQuery<HTMLElement>[] | (() => JQueryInput | JQuery.Promise<any>) | PromiseLike<JQueryInput>;

export interface ExposedElementData
{
    element: JQuery<HTMLElement> | HTMLElement;
    previousOpacity?: string;
    previousPosition?: string;
    previousZIndex: string;
}

export type BeforeCloseFunction = ((layer: ModalLayer, reason?: CloseReason) => void | boolean | PromiseLike<void> | PromiseLike<boolean>) | ((layer: ModalLayer) => void | boolean | PromiseLike<void> | PromiseLike<boolean>) | (() => void | boolean | PromiseLike<void> | PromiseLike<boolean>) | null;
export type AfterCloseFunction = ((layer?: ModalLayer, reason?: CloseReason) => void) | (() => void | boolean) | null;

export enum CloseReasons {
    Escape = "escape",
    Tab = "tab",
    GlassClick = "glassClick",
    SelectedItem = "selectedItem",
}
export type CloseReason = CloseReasons | (string & Record<never, never>);

interface ButtonGroups
{
    right: JQuery<HTMLElement>[];
    left: JQuery<HTMLElement>[];
    mobileClose?: JQuery<HTMLElement>;
}

interface ReferenceAreaItem
{
    label: string;
    data: string;
}

interface MinimumHeightInfo
{
    rowsFoundForMinHeight?: number;
    heightOfOneRow?: number;
    heightOfFoundMinRows?: number;
}

interface BrowsePositioningCache
{
    browseID?: string;
    browseControlsHeight?: number;
    browseHeaderHeight?: number;
    positionBelowAnchorTo?: boolean;
}

interface AnchoredLayerCssPosition
{
    top?: number | string;
    left?: number | string;
}

interface MobileEelParts
{
    modal:
    {
        origPosition: Partial<CSSStyleDeclaration>;
        newPosition: Partial<CSSStyleDeclaration>;
        skipAfterClose: boolean;
        element?: JQuery<HTMLElement>;
    };
    textbox:
    {
        element: JQuery<HTMLElement>;
        origStyle: string;
        placeholder: JQuery<HTMLElement>;
        afterShowAnimate: () => void;
        origPosition: Partial<CSSStyleDeclaration>;
        newPosition: Partial<CSSStyleDeclaration>;
    };
}

const validRoles =
[
    "dialog",
    "alertdialog",
    "menu",
    "listbox"
];

export enum ButtonText
{
    Save = "Save",
    Cancel = "Cancel",
    Close = "Close",
    Done = "Done",
    Ok = "Ok",
    Select = "Select"
}

export enum ButtonIcon
{
    Save = "Save",
    Cancel = "Cancel",
    Close = "Close",
    Check = "Check",
    Select = "Select"
}

export const DEFAULT_CLOSE_BUTTON = Object.freeze(
{
    text: ButtonText.Close,
    icon: ButtonIcon.Close,
    click: "close"
});

export const DEFAULT_CANCEL_BUTTON = Object.freeze(
{
    text: ButtonText.Cancel,
    icon: ButtonIcon.Cancel,
    click: "close"
});

export const DEFAULT_OK_BUTTON = Object.freeze(
{
    text: ButtonText.Ok,
    icon: ButtonIcon.Check,
    click: "close"
});

export const closeButton: Readonly<ButtonDefinition> =
{
    ...DEFAULT_CLOSE_BUTTON,
    id: null,
    disabled: false,
    focus: true,
    additionalClasses: ""
};

/**
 * Shows a Reload Dialog
 *
 * @param header Text to display at the top of the dialog
 * @param contents The content of the dialog
 */
export function showReloadDialog(header: string, contents: ModalContents | null)
{
    contents ??= "An error occurred. Please try reloading the screen.";

    const buttons: ButtonDefinition[] =
    [
        { text: "Reload", icon: "Redo", click: function () { reloadScreen(); } },
        { text: ButtonText.Close, icon: ButtonIcon.Close, click: function () { dialog.close(); } }
    ];

    const options =
    {
        closeOnClick: false
    };

    const dialog = showDialog(header, contents, buttons, options);
}

/**
 * Opens an Error dialog (Deprecated)
 *
 * @param title Text to display at the top of the dialog
 * @param contents The content of the dialog
 * @param buttons An array of buttons to add to the dialog
 * @param options A configuration object for the dialog
 * @returns The ModalDialog for the dialog that was opened
 *
 * @deprecated
 */
export function showCriticalDialog(title: string, contents: ModalContents, buttons?: ButtonDefinitions | null, options?: ModalDialogSettings)
{
    options = concatAdditionalClasses(options, "critical");
    options.role = "alertdialog";

    return createModalDialog(title, contents, buttons, options);
}

/**
 * Show a standard warning dialog with confirm and cancel options.
 * The message will be "<b>Are you sure you want to ${action}</b> ${description}",
 * @param action The action that is being warned about
 * @param description A detailed description
 * @param optionArg Additional options
 * @returns A promise that will be resolved to true, if the warning was accepted, or false, if it was canceled.
 */
export function showWarningDialog(action: string, description: string, optionArg?: { width?: number, onConfirm?: () => void, onCancel?: () => void, id?: string; })
{
    const options = $.extend({ width: 450 }, optionArg);
    const escapedDescription = encodeHtml(description).replace(/\n/g, "<br/>");
    const contents = [
        $("<p>", { addClass: "bold", text: "Are you sure you want to " + action + "?" }),
        $("<p>&nbsp;</p>"),
        $("<p>", { html: escapedDescription })
    ];

    const approvedReasonText = "approve";
    const dialogResult = $.Deferred();

    const buttons: ButtonDefinition[] =
    [
        { text: action.capitalize(), icon: ButtonIcon.Check, click: function () { dialog.close(approvedReasonText); } },
        { text: ButtonText.Cancel, icon: ButtonIcon.Cancel, click: function () { dialog.close("cancel"); } }
    ];

    const dialog = createModalDialog("Warning", contents, buttons,
        {
            id: options.id,
            width: options.width.toString(),
            additionalClasses: "warning",
            debugName: "Warning",
            role: "alertdialog",
            afterClose: function (layer)
            {
                const isConfirmed = layer?.reasonForClose === approvedReasonText;
                if (isConfirmed)
                {
                    if (typeof options.onConfirm === "function")
                    {
                        options.onConfirm();
                    }
                    dialogResult.resolve();
                    return;
                }

                dialogResult.reject(new CancellationError("Canceled warning dialog"));
            }
        }
    );

    return dialogResult.promise();
}

/**
 * Show a standard error dialog.
 * The message will be "<b>${message}</b> ${description}", and the only button will be a close button.
 * @param message The error
 * @param description A detailed description of the error
 * @param optionArg Additional options
 * @returns A promise that will be resolved when the dialog is closed.
 */
export function showErrorDialog(message: string, description: string, optionArg?: { width?: number, id?: string, afterClose?: (layer?: ModalLayer, reason?: string) => void })
{
    const options = $.extend({ width: "450" }, optionArg);
    const escapedDescription = encodeHtml(description).replace(/\n/g, "<br/>");
    const contents =
    [
        $("<p>", { addClass: "bold", text: message }),
        $("<p>&nbsp;</p>"),
        $("<p>", { html: escapedDescription })
    ];

    const dialogResult = $.Deferred();

    const cancelButton: ButtonDefinition =
    {
        text: ButtonText.Close,
        icon: ButtonIcon.Close,
        click: function (_evt: JQuery.ClickEvent, dialog: ModalDialog)
        {
            dialog.close();
            dialogResult.resolve(true);
        }
    };

    createModalDialog("Error", contents, [cancelButton],
        {
            id: options.id,
            width: options.width,
            additionalClasses: "critical",
            debugName: "Error",
            role: "alertdialog",
            beforeClose: function (_layer, reason)
            {
                if (reason === CloseReasons.Escape || reason === CloseReasons.GlassClick)
                {
                    dialogResult.resolve(false);
                }
            },
            afterClose: options.afterClose
        }
    );

    return dialogResult.promise();
}

/**
 * Shows a basic modal dialog
 * @param title Text to display at the top of the dialog
 * @param contents The content of the dialog
 * @param buttons An array of buttons to add to the dialog. **Notice:** If button labels or icons are appearing with different labels or icons, checkout `validateButtonDefinitions()`
 * @param options A configuration object for the dialog
 * @returns The ModalDialog for the dialog that was opened
 */
export function showDialog(title: string, contents: ModalContents, buttons?: ButtonDefinitions, options?: ModalDialogSettings)
{
    options = concatAdditionalClasses(options, "standard");

    return createModalDialog(title, contents, buttons, options);
}

/**
 * Returns a JQuery collection of modal layer containers that exist in the DOM
 * @returns A JQuery collection of open modal layers
 */
export function getOpenModalLayers()
{
    return $('.modalLayerContainer');
}

/**
 * Close the ModalLayer
 * @param modalLayerSelector The ModalLayer to close
 * @returns Returns true if the dialog closed
 */
export function closeModalLayer(modalLayerSelector: JQueryInput)
{
    const modalLayerElement = $(modalLayerSelector);
    if (!modalLayerElement.length)
    {
        return false;
    }
    const closeElement = modalLayerElement.find("#cancelButton,.informationDialogClose,.modalContentFooter svg.close,.glass").last();
    if (closeElement.length)
    {
        closeElement.trigger("click");
        return true;
    }
    return false;
}

/**
 * Opens a modal dialog with Delete/Cancel buttons, then awaits the user's response.
 * @param nameOfItemToDelete A string to display as the name of the thing to delete in the message that is displayed.
 * @param additionalMessage Additional text to display underneath the delete message.
 * @param optionsArg Options for the delete dialog.
 * @returns A promise object, which is resolved or rejected when the user clicks yes or no, respectively.
 *          Note: Script execution continues immediately. Use .done(), .fail(), and .always() to respond at the proper time.
 */
export function confirmDelete(nameOfItemToDelete: string, additionalMessage?: string, optionsArg?: { referenceItems: ReferenceAreaItem[] })
{
    // TODO DOCUMENT THIS OPTION TYPE
    let options = $.extend({}, optionsArg);
    const waitForConfirmation = $.Deferred();

    const confirmButton: ButtonDefinition =
    {
        text: "Yes, Delete",
        icon: "CheckmarkConfirm",
        click: waitForConfirmation.resolve,
        additionalClasses: "confirmDelete_doDelete",
        additionalSvgClasses: "confirmDelete_doDeletePart",
        additionalTextClasses: "confirmDelete_doDeletePart"
    };

    const cancelButton: ButtonDefinition =
    {
        text: "No, Cancel",
        icon: ButtonIcon.Cancel,
        click: () => waitForConfirmation.reject(new CancellationError("Delete cancelled")),
        additionalClasses: "confirmDelete_cancel",
        additionalSvgClasses: "confirmDelete_cancelPart",
        additionalTextClasses: "confirmDelete_cancelPart"
    };

    let message = `<span class="modalContent__deleteHeading">Are you sure you want to delete this ${nameOfItemToDelete}?</span>`;

    if (additionalMessage != null)
    {
        message += `<div class="modalContent__deleteAdditionalText">${additionalMessage}</div>`;
    }

    const referenceAreaHTML = getReferenceAreaHTML(options.referenceItems);
    if (referenceAreaHTML != null)
    {
        message = `${referenceAreaHTML}${message}`;
    }

    const dialog = showCriticalDialog("CONFIRM DELETE", message, [confirmButton, cancelButton], { closeOnClick: false, closeOnEscape: false });
    return waitForConfirmation.always(dialog.close).promise();
}

export function getReferenceAreaHTML(referenceItems: ReferenceAreaItem[])
{
    if (referenceItems && Array.isArray(referenceItems) && referenceItems.length > 0)
    {
        const refItems = referenceItems.map(item =>
        {
            return `<span class="reference-slats static"><span class="slatLabel">${item.label}</span><span class="slatData">${item.data}</span></span>`;
        });
        return `<div class="referenceBar">${refItems.join("")}</div>`;
    }
    return null;
}

/**
 * Shows a Touch Friendly dialog
 * @param title Text to display at the top of the dialog
 * @param contents The content of the dialog
 * @param leftButtons An JQuery collection of buttons to add to the left dialog button area
 * @param rightButtons An JQuery collection of buttons to add to the right dialog button area
 * @param options A configuration object for the dialog
 * @returns The ModalDialog for the dialog that was opened
 */
export function showTouchFriendlyDialog(title: string, contents: ModalContents, leftButtons: ButtonDefinitions, rightButtons: ButtonDefinitions, options?: ModalDialogSettings)
{
    const buttons: ButtonDefinition[] = [];

    setFrameworkClassname(leftButtons, "left");
    setFrameworkClassname(rightButtons, "right");

    options = concatAdditionalClasses(options, "standard");

    return createModalDialog(title, contents, buttons, options);

    function setFrameworkClassname(definitions: ButtonDefinitions, position: string)
    {
        const frameworkClass = " modalContentFooter__button--touchFriendly js--modalContentFooter__button--" + position;
        $(definitions).each(function (this)
        {
            const button = this as ButtonDefinition;
            buttons.push(button);

            concatAdditionalClasses(button, frameworkClass);
        });
    }
}

/**
 * Opens a CodeEEl dialog
 * @param popupContent The element that will be used as the content of the dialog
 * @param textboxToFocus The code eel textbox to place focus in when the dialog opens
 * @param containerToExpose The element to expose through the glass of the dialog
 * @param beforeClose A function that will be called before the dialog closes.  Return false to prevent the dialog from closing.
 * @param eelData An object that contains data relevant to the code eel
 * @param bindings An array of bindings objects
 * @param afterClose A function that will be called after the dialog closes.
 * @returns The ModalLayer for the code eel dialog that was opened
 */
export function showCodeEELDialog(popupContent: JQueryInput, textboxToFocus: JQuery<HTMLElement>, containerToExpose: JQueryInput, beforeClose: BeforeCloseFunction, eelData: CodeEELData | DistinctMultiSelectEELData, bindings?: Array<any>, afterClose?: AfterCloseFunction)
{
    if (mobileAccess)
    {
        return showMobileEelDialog.apply(undefined, arguments as any) as ModalLayer;
    }

    let initialFocusElementSelector = undefined;
    if ('codeEELValue' in eelData)
    {
        const value = eelData.codeEELValue.val() as string;
        if (value && value !== "")
        {
            initialFocusElementSelector = ".browseRow.codeEEL__browseRow--selected .button.js-selectRecord"
        }
        else
        {
            initialFocusElementSelector = ".browseRow.current .button.js-selectRecord"
        }
    }

    eelData.layer = newModalLayer(popupContent, beforeClose,
        {
            initialFocusElement: initialFocusElementSelector,
            bindings,
            expose: [containerToExpose],
            tailessAnchored: true,
            afterClose
        });

    const positioningOptions =
    {
        minRowsToShow: 3,
        minimumBrowseWidth: 500,
        maintainRelativePosition: true
    };

    const positioningManager = createModalPositioningManager(eelData.layer, containerToExpose, eelData.layer.contents, positioningOptions);
    eelData.layer.options.loadDOMCallback = positioningManager.positionEELContents;
    eelData.layer.options.resizeCallback = positioningManager.positionEELContents;
    eelData.layer.show();

    if (textboxToFocus)
    {
        textboxToFocus.trigger("focus");
    }

    return eelData.layer;
}

/**
 * Shows a Tile View Results dialog
 * @param popupContent The element that will be used as the content of the dialog
 * @param searchBox The element to expose through the glass of the dialog and anchor the dialog to
 * @param beforeClose A function that will be called before the dialog closes.  Return false to prevent the dialog from closing.
 * @param bindings An array of bindings objects
 * @returns The ModalLayer that was created
 */
export function showTileViewResults(popupContent: JQueryInput, searchBox: JQueryInput, beforeClose?: BeforeCloseFunction, bindings?: Array<any>)
{
    const layer = newModalLayer(popupContent, beforeClose, { bindings: bindings, expose: [searchBox], preserveFocus: true, role: "listbox" });

    const positioningOptions =
    {
        getRows: function () { return layer.contents.find('.templatedResult, .toolbar--browseControls.tileviewEEL'); },
        getHeaderRow: function () { return layer.contents.find('.templatedResult').first(); }
    };

    const positioningManager = createModalPositioningManager(layer, searchBox, layer.contents, positioningOptions);

    layer.options.resizeCallback = positioningManager.positionEELContents;

    registerLayoutResizeCallback(function ()
    {
        if (layer.options.resizeCallback)
        {
            layer.options.resizeCallback();
        }
    });
    return layer;
}

/**
 * Shows an Information dialog
 * @param title Text to display at the top of the dialog
 * @param contents The content of the dialog
 * @param buttons An array of buttons to add to the dialog
 * @param options A configuration object for the dialog
 * @returns The ModalDialog for the dialog that was opened
 */
export function showInformationDialog(title: string, contents: ModalContents, buttons?: ButtonDefinitions | null, options?: ModalDialogSettings)
{
    const _options = concatAdditionalClasses(options, "standard");

    const dialog = createModalDialog(title, contents, buttons, _options);
    if (!dialog.title.find('.informationDialogClose').length)
    {
        const informationDialogClose = $("<a>", { href: 'javascript:void(0)', class: 'informationDialogClose', role: 'button', 'aria-label': "Close" });
        informationDialogClose.attr("tabindex", 0);

        const closeSvg = getSVG("Close", "MessageBox")!.addClass("svgNoHover");
        informationDialogClose.append(closeSvg);

        dialog.title.addClass("hasClose");
        dialog.title.append(informationDialogClose);

        informationDialogClose.on('click', function ()
        {
            dialog.close();
        });

        informationDialogClose.on('keydown', function (event: JQuery.KeyDownEvent)
        {
            const eventKeys = skyward.keys.getKeyNamesFromEvent(event as JQuery.KeyboardEventBase);
            if (eventKeys.ENTER)
            {
                dialog.close();
                event.preventDefault();
                event.stopPropagation();
            }
        });
    }

    dialog.content.addClass('noFooter informationDialogContent');
    dialog.footer.remove();

    return dialog;
}

/**
 * Shows an Information Bubble dialog
 * @param title Text to display at the top of the dialog
 * @param contents The content of the dialog
 * @param buttons An array of buttons to add to the dialog
 * @param options A configuration object for the dialog
 * @returns The ModalDialog for the dialog that was opened
 */
export function showInformationBubble(title: string, contents: ModalContents, buttons?: ButtonDefinitions, options?: ModalDialogSettings)
{
    options = concatAdditionalClasses(options, "standard");

    const dialog = createModalDialog(title, contents, buttons, options);
    dialog.content.addClass('noFooter');
    dialog.footer.remove();
    dialog.container.addClass('noFooter');
    return dialog;
}

/**
 * Shows a Processing dialog
 * @param title Text to display at the top of the dialog
 * @param contents The content of the dialog
 * @param buttons An array of buttons to add to the dialog
 * @param options A configuration object for the dialog
 * @returns The ModalDialog for the dialog that was opened
 */
export function showProcessingDialog(title: string, contents: ModalContents, buttons?: ButtonDefinitions, options?: ModalDialogSettings)
{
    options = concatAdditionalClasses(options, "standard");

    options.automationID = 'processingDialog';
    const dialog = createModalDialog(title, contents, buttons, options);

    if (!mobileAccess)
    {
        const helpCenterWorkflowButton = $('.js-workflowHelpCenterButton');
        const buttonContainer = $('<div>',
        {
            class: 'messageBoxTitle__buttonContainer'
        });

        if (helpCenterWorkflowButton.length > 0)
        {
            const helpCenterButton = $('<a>',
            {
                class: 'messageBoxTitleButton',
                href: helpCenterWorkflowButton.prop('href') ?? 'https://hub.skyward.com/sso/helpcenter/qproxy',
                target: '_blank'
            });

            const helpCenterText = $('<div class="messageBoxTitleButton__text">Help<br/>Center</div>');
            helpCenterButton.append(helpCenterText);

            const helpCenterSvg = getSVG("Help", "MessageBox")!.addClass("messageBoxTitleButton__svg svgNoHover");
            helpCenterButton.prepend(helpCenterSvg);
            buttonContainer.append(helpCenterButton);
        }

        const newWindowButton = $('<a>',
        {
            class: 'messageBoxTitleButton',
            href: 'javascript:void(0)'
        });
        const newWindowText = $('<div class="messageBoxTitleButton__text">New<br/>Window</div>');
        newWindowButton.append(newWindowText);

        const newWindowSvg = getSVG("NewWindow", "MessageBox")!.addClass("messageBoxTitleButton__svg svgNoHover");
        newWindowButton.prepend(newWindowSvg);
        buttonContainer.append(newWindowButton);

        dialog.title.append(buttonContainer);

        newWindowButton.on('click', function ()
        {
            const url = window.location.pathname;
            let urlArr: Array<string | null> = [];

            if (url != null)
            {
                urlArr = url.split('/');
                if (urlArr.length <= 5)
                {
                    urlArr![5] = null;
                }
            }

            loadContent('popout', urlArr[2]!, urlArr[3]!, urlArr[4] || "index", urlArr[5]);
        });
    }

    return dialog;
}

/**
 * Shows a menu dialog
 * @param menuItems An array of menu item objects that define the items in the menu
 * @param anchorTo The element that the menu will be anchored to
 * @param options A configuration object for the menu
 * @returns The ModalMenu that was created
 */
export function showMenu(menuItems: MenuItemDefinition[], anchorTo: JQueryInput, options?: ModalMenuSettings)
{
    return createModalMenu(menuItems, anchorTo, options);
}

/**
 * Adds events to the dialog container to prevent it from being moved beyond the edge of the screen.
 * @param container A string set draggable limits on
 */
export function setupDraggableLimits(container: JQuery<HTMLElement>)
{
    // Set draggable containment. Stop dragging when mouse hits edge of window or when draggable
    // will be less than the specified minimum pixels on screen.
    container.on('mousedown', function (event)
    {
        const master = $('#master');
        const minimumOnScreen = container.find('.modalHeader').outerHeight()!;

        const popupOffset = container.offset()!;
        const mouseOffset =
        {
            top: event.pageY - popupOffset.top,
            left: event.pageX - popupOffset.left
        };

        const masterWidth = master.width()!;
        const masterHeight = master.height()!;

        const minimumX1 = (container.width()! - minimumOnScreen) * -1;
        const maximumX2 = masterWidth - minimumOnScreen;
        const maximumY2 = masterHeight - minimumOnScreen;

        let x1 = mouseOffset.left * -1;
        const y1 = 0;
        let x2 = masterWidth - mouseOffset.left;
        let y2 = masterHeight - mouseOffset.top;

        if (x1 < minimumX1)
        {
            x1 = minimumX1;
        }

        if (x2 > maximumX2)
        {
            x2 = maximumX2;
        }

        if (y2 > maximumY2)
        {
            y2 = maximumY2;
        }

        container.draggable('option', 'containment', [x1, y1, x2, y2]);
    });
}

/**
 * Backbone API to define, create, bind, handle and destroy any modal interface components
 * @param contents Element to be inserted into the new modal layer
 * @param beforeClose Function to be called before layer closes. Return (false) to keep the layer
 * @param options Advanced options - See documentation at head of function
 * @returns The completed 'layer object', which contains references to all elements, functions, and objects needed during the lifespan of the layer
 */
export function newModalLayer(contents: JQueryInput, beforeClose?: BeforeCloseFunction, options?: ModalLayerOptions)
{
    // Establish layer data
    const layerId = $(document.body).children(".modalLayerContainer").length;
    const optionsToUse: ModalLayerOptions = $.extend({}, getDefaultModalLayerOptions(), options)
    let layerPseudoForm: JQuery<HTMLElement> | undefined = undefined;

    let layer: ModalLayer =
    {
        _bindingPoints: [],
        _exposedByUnderlyingLayer: [],
        _exposedElements: [],
        setupContent: setupContentForLayer,
        addContent: addContentToLayer,
        beforeClose: beforeClose,
        close: closeLayer,
        container: $("<div>", { class: "modalLayerContainer", "data-layerid": layerId }),
        contents: $(),
        glass: $(optionsToUse.glass!).attr("id", "modalLayerGlass" + layerId),
        onscreen: false,
        closing: false,
        options: optionsToUse,
        originalActiveElement: document.activeElement as HTMLElement,
        show: showLayer,
        uniqueId: layerId
    };

    // Check the haspopup value on layers that were opened from buttons
    if (layer.originalActiveElement?.getAttribute("role") === "button" || layer.originalActiveElement?.getAttribute("aria-haspopup") !== null)
    {
        const haspopupValue = layer.originalActiveElement.getAttribute("aria-haspopup");
        if (layer.options.role !== haspopupValue && isDebug)
        {
            // This doesn't quite work, originalActiveElement can sometimes not be the button that cause the modal to open, due to the modal the button was in no longer being in the DOM
            //skyward.error.reportException(new window.SkywardException("Modal role doesn't match button markup",
            //    "The role of the opened modal doesn't match the aria-haspopup markup of the button that opened it.\n\n" +
            //    `Modal Role: ${layer.options.role}\nButton HasPopup: ${haspopupValue}\nButton Id: ${layer.originalActiveElement.id}\n` +
            //    `Button Classes: ${layer.originalActiveElement.className}\nButton Click Action: ${JSON.stringify(layer.originalActiveElement.getAttribute("data-click-action"))}`,
            //    window.Severity.Debug,
            //    "24C4628E-19E8-4BA4-B525-A175AFE3C940"));
        }
    }

    if (layer.options.tailessAnchored)
    {
        layer.options.anchored = true;
    }

    if (layer.options.loadDOMCallback)
    {
        registerLoadDOMCallback(layer.options.loadDOMCallback);
    }

    if (options != null && options.transparent == true)
    {
        $(layer.glass).addClass("transparent");
    }

    layer.container.appendTo(document.body);

    layer.container.data("_modalLayerDebug",
        {
            name: layer.options.debugName,
            creator: getCallerLocation()
        });

    // To define a custom element for use as this modal layer's glass (the barrier separating this layer from it's older siblings),
    //      provide the html to render the glass in the options parameter as options.glass
    //          (i.e. {glass:"<div class='notNormalGlassClass'/>"})
    // Note that the id of a layer's glass will ALWAYS be 'modalLayerGlass##' (where ## is the layer id#)
    layer.addContent($(layer.glass));

    // In some situations, you may need to "expose" elements from the original view, making them clickable on the new modal layer.
    // To do this, create an array of the elements to expose (either as jQuery objects, DOM elements, or jQuery selector strings)
    // and pass this array to modalLayer.newModalLayer as options.expose[].
    // (More info inside closure)
    //Expose DOM elements

    // Due to z-indexing recurring nightmares, all *currently* non-exposed elements must have no z-index
    $(".currentlyExposed").each(function ()
    {
        const jqThis = $(this);
        layer._exposedByUnderlyingLayer.push(
            {
                "element": jqThis,
                "previousZIndex": jqThis.css("z-index")
            });

        jqThis.css("z-index", "auto").removeClass("currentlyExposed");
    });

    if (layer.options.expose)
    {
        for (const x of layer.options.expose)
        {
            const exposedClassName = "exposedToModalLayer_" + layer.uniqueId;
            $(x)
                .addClass(exposedClassName)
                .addClass("currentlyExposed")
                .each(function ()
                {
                    const jqThis = $(this);
                    layer._exposedElements.push(
                        {
                            "element": jqThis,
                            "previousOpacity": jqThis.css("opacity"),
                            "previousPosition": jqThis.css("position"),
                            "previousZIndex": jqThis.css("z-index")
                        });

                    // (MattE) Opacity explicitly set to create new "stacking context". For more info, see w3c spec on opacity
                    // or refer to http://philipwalton.com/articles/what-no-one-told-you-about-z-index/
                    jqThis.css({ "z-index": 1, "position": "relative", "opacity": 0.999999 });

                    jqThis.find(":focusable").addClass(exposedClassName);

                    layer._bindingPoints.push(jqThis.parent());

                    jqThis.on("wheel", preventScrollingOnExposedElements);
                });
        }
    }

    layer.contents = $(contents);
    layer.addContent(layer.contents);

    // Establish bindings
    // To bind custom events to the layer's contents, define each binding inside options.bindings[]
    //  Example:
    //          myLayerOptions = {
    //              bindings: [
    //                  {onEvents:"click", inSelectors:".button", runFunction: myCustomButtonClickHandler},
    //                  {onEvents:"keypress", inSelectors:"input", runFunction: myCustomFilterFunction}
    //              ]};
    //          newModalLayer(myContent, null, myLayerOptions)
    //
    // Note: Because bindings are attached to the *container* through jQuery.on(), your elements do NOT need to be attached to the DOM at bind-time :)

    // A binding is defined as {onEvents, inSelectors, runFunction [, directBind]}
    // onEvents:
    //      Space-delimited list of events which will trigger this binding. i.e. "click keydown keypress"
    // inSelectors:
    //      A jQuery-style selector to filter the descendants of the layer container. i.e. "span.clickable", "a, input.clickable", etc
    //      The descendants in question do NOT have to exist in the DOM at bind-time for these bindings to work.
    //      (This is a feature built into jQuery.on(), read about it at http://api.jquery.com/on/ )
    // runFunction:
    //      The function to call when one of these events bubbles to the container from an element matching the selector.
    // bindTo:
    //      To specify an element other than the layer container as the binding-element for an event-handler, set this value to
    //      either a jQuery object containing the reference(s) to the element, or a selector string.
    if (layer.options.closeOnClick === true || !mobileAccess)
    {
        layer.glass.on("click", function ()
        {
            if (!layer)
            {
                return;
            }

            if (layer.options.closeOnClick === true)
            {
                layer.close('glassClick');
            }
            else if (!mobileAccess)
            {
                setFocus(layer);
            }
        });
    }

    if (layer.options.preventKeyBubbling)
    {
        layer.container.on("keydown keyup" as any, function (event: JQuery.KeyboardEventBase)
        {
            const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
            return eventKeys.TAB || eventKeys.ENTER;
        });
    }

    if (layer.options.closeOnEscape === true)
    {
        layer.options.bindings = layer.options.bindings?.slice();
        layer.options.bindings?.push(
            {
                onEvents: 'keydown',
                bindTo: document.body,
                runFunction: function (event: JQuery.KeyboardEventBase)
                {
                    const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
                    if (eventKeys.ESC)
                    {
                        const escEvent = (event as any) as { currentPopupId?: number | null };
                        if (escEvent.currentPopupId === undefined || escEvent.currentPopupId === null)
                        {
                            escEvent.currentPopupId = getCurrentPopupId();
                        }

                        if (layer.uniqueId === escEvent.currentPopupId)
                        {
                            layer.close('escape');
                            event.stopImmediatePropagation();
                        }
                        return false;
                    }
                    return true;
                }
            });
    }

    if (layer.options.bindings?.length)
    {
        layer._bindingPoints.push(layer.container);
        layer._bindingPoints = $($.uniqueSort(layer._bindingPoints as unknown as HTMLElement[])) as unknown as JQuery<HTMLElement>[];
        for (const binding of layer.options.bindings)
        {
            let bindingPoints: JQuery<HTMLElement> | JQuery<HTMLElement>[] | undefined;
            if (binding.bindTo !== undefined)
            {
                bindingPoints = $(binding.bindTo);
            }
            if (bindingPoints?.length === 0)
            {
                bindingPoints = layer._bindingPoints;
            }

            if (binding.inSelectors)
            {
                //@ts-ignore jQuery types are not playing nice here.
                (bindingPoints as JQuery<HTMLElement> | undefined)?.on(binding.onEvents, binding.inSelectors, null, binding.runFunction);
            }
            else
            {
                //@ts-ignore jQuery types are not playing nice here.
                (bindingPoints as JQuery<HTMLElement> | undefined)?.on(binding.onEvents, binding.runFunction);
            }
        }
    }

    layer.container.data("layer", layer);
    layer.onscreen = true;

    if (!mobileAccess)
    {
        registerLayer(layer);
    }

    return layer;

    function showLayer()
    {
        if (!layer)
        {
            reportException(new SkywardException("layer is undefined within show function.", `layerId: ${layerId}`));
            return;
        }
        if (layer.options.anchored)
        {
            layer.contents.addClass('anchored');
            if (layer.options.tailessAnchored)
            {
                layer.contents.addClass('noAnchoredTail');
            }
        }
        layer.contents.addClass('columnLayoutWrapper--modalLayer');
        layer.container.show();
        if (layer.options.loadDOMCallback && typeof layer.options.loadDOMCallback === 'function')
        {
            registerLoadDOMCallback(layer.options.loadDOMCallback);
        }
        if (layer.options.resizeCallback && typeof layer.options.resizeCallback === 'function')
        {
            registerLayoutResizeCallback(layer.options.resizeCallback);
        }
        if (!mobileAccess)
        {
            setFocus(layer);
        }
    }

    function setupContentForLayer(this: ModalLayer, content: JQuery<HTMLElement>)
    {
        if (!content.hasClass("glass"))
        {
            content.addClass("modalLayer");
            if (options?.additionalContainerClasses)
            {
                content.addClass(options.additionalContainerClasses);
            }
            if (!content.attr("role"))
            {
                content.attr("role", validateAriaRole(layer.options.role!));
            }
            if (!content.attr("aria-modal"))
            {
                content.attr("aria-modal", "true");
            }
            // The layerPseudoForm is used for adding role=form to dialogs when appropriate for accessibility
            if (layerPseudoForm === undefined)
            {
                layerPseudoForm = $("<div>", { class: "layerPseudoForm" });
                this.container.append(layerPseudoForm);
                return layerPseudoForm;
            }
        }
        return this.container;
    }

    function addContentToLayer(this: ModalLayer, content: JQuery<HTMLElement>)
    {
        const targetContainer = setupContentForLayer.call(this, content);
        targetContainer.append(content);
        $.extend(layer.contents, content);
    }

    /**
     * Check if layer is topmost, run "beforeClose"
     * Note: Layer.reasonForClose will contain 'escape' or 'glassClick' when those actions are responsible for closing the window.
     * @param reason A custom reason, code, or other data to be attached to Layer.reasonForClose by passing it into close
     * @returns
     */
    function closeLayer(reason?: string)
    {
        if (!layer || layer.closing)
        {
            return false;
        }

        layer.reasonForClose = reason;
        layer.closing = true;

        // Run beforeClose() if defined
        if (typeof layer.beforeClose === "function")
        {
            if (layer.beforeClose(layer, reason) === false)
            {
                layer.reasonForClose = undefined;
                layer.closing = false;
                return false;
            }
        }

        if (layer.options.bindings)
        {
            //Remove bindings
            for (const binding of layer.options.bindings)
            {
                let bindingPoints: JQuery<HTMLElement> | JQuery<HTMLElement>[] | undefined;
                if (binding.bindTo !== undefined)
                {
                    bindingPoints = $(binding.bindTo);
                }
                if (bindingPoints?.length === 0)
                {
                    bindingPoints = layer._bindingPoints;
                }

                if (binding.inSelectors)
                {
                    //@ts-ignore jQuery types are not playing nice here.
                    (bindingPoints as JQuery<HTMLElement> | undefined)?.off(binding.onEvents, binding.inSelectors, binding.runFunction);
                }
                else
                {
                    //@ts-ignore jQuery types are not playing nice here.
                    (bindingPoints as JQuery<HTMLElement> | undefined)?.off(binding.onEvents, binding.runFunction);
                }
            }
        }

        let elementData;
        //re-expose elements from original DOM
        for (const exposedElement of layer._exposedElements)
        {
            elementData = exposedElement;
            $(elementData.element).css(
                {
                    zIndex: elementData.previousZIndex,
                    position: elementData.previousPosition,
                    opacity: elementData.previousOpacity
                } as JQuery.PlainObject)
                .removeClass("exposedToModalLayer_" + layer.uniqueId)
                .removeClass("currentlyExposed");

            $(elementData.element).off("wheel", preventScrollingOnExposedElements);
        }

        for (const exposedElement of layer._exposedByUnderlyingLayer)
        {
            elementData = exposedElement;
            $(elementData.element).css(
                {
                    zIndex: elementData.previousZIndex
                })
                .addClass("currentlyExposed");
        }

        if (!mobileAccess)
        {
            const preserveFocus = typeof layer.options.preserveFocus === "function" ? layer.options.preserveFocus.call(layer, reason!) : layer.options.preserveFocus;
            unregisterLayer(layer, preserveFocus);
        }

        layer.container.removeData("layer");
        layer.container.off();
        layer.container.remove();
        layer.onscreen = false;

        if (typeof layer.options.afterClose === 'function')
        {
            layer.options.afterClose(layer, reason);
        }

        if (layer.options.resizeCallback && typeof layer.options.resizeCallback === 'function')
        {
            removeLayoutResizeCallback(layer.options.resizeCallback);
        }

        if (layer.options.loadDOMCallback && typeof layer.options.loadDOMCallback === 'function')
        {
            removeLoadDOMCallback(layer.options.loadDOMCallback);
        }

        //@ts-ignore This is after closing the Modal Layer, so it's okay to set to null
        layer = null;

        return true;
    }
}

/**
 * Retrieve the ModalLayer object by passing in an element within the modal.
 * @param sourceElement An element/selector within a modal
 * @returns The ModalLayer for the provided element
 */
export function getLayerFromElement(sourceElement: JQueryInput)
{
    const element = $(sourceElement);
    const container = element.hasClass("modalLayerContainer") ? element : element.closest(".modalLayerContainer");
    return container.data("layer") as ModalLayer | undefined;
}

/**
 * Creates a positioning manager to control where a modal dialog is positioned
 * @param dialog A ModalDialog that will have its contents positioned
 * @param anchorToSelector The element to anchor the ModalDialog.contents to
 * @param content A reference to the ModalDialog.contents
 * @param positioningOptions An object with configuration options for the PositioningManager
 * @returns A PositioningManager object
 */
export function createModalPositioningManager(dialog: ModalDialog | ModalMenu | ModalLayer, anchorToSelector: JQueryInput, content: JQuery<HTMLElement>, positioningOptions?: Partial<PositioningOptions>)
{
    const ANCHORED_TAIL_HEIGHT = 13;
    const MINIMUM_CONTENT_OUTER_HEIGHT = 60;
    const WINDOW_PADDING = 20;

    const positioningSettings = $.extend(
        {
            maintainRelativePosition: false
        }, positioningOptions);

    const cache: BrowsePositioningCache = {};
    const container = dialog.container;
    const minimumContentCssHeight = parseInt(content.css('min-height'));

    let canSetHeight = false;
    let positionBelowAnchorTo = null;
    const anchorTo = $(anchorToSelector);

    const positioningManager: PositioningManager =
    {
        measurements:
        {
            allowedContentHeight: 0,
            contentOnly: 0,
            containerWithoutContent: 0,
            entireContainer: 0,
            top: 0
        },
        isAnchored: function ()
        {
            return (anchorTo.length && anchorTo.is(":visible")) as boolean;
        },
        positionModalContents: function ()
        {
            positioningManager.isAnchored() ?
                positionAnchoredLayer() :
                positionUnanchoredLayer();
        },
        positionEELContents: function ()
        {
            positioningManager.isAnchored() ? setAnchorToOffset() : dialog.close();

            const eelPositioningSettings = $.extend(
                {
                    getHeaderRow: function ()
                    {
                        if (browse.header === undefined || browse.header === null)
                        {
                            return $();
                        }
                        return browse.header.find(".unlockedHeaderTableRow");
                    },
                    getRows: function ()
                    {
                        if (browse.browse === undefined || browse.browse === null)
                        {
                            return $();
                        }
                        return browse.browse.find(".js-eelPositionManagerGetRowsForHeight");
                    },
                    minimumBrowseWidth: 0,
                    minContentHeight: 50
                },
                positioningSettings);

            content.width("");
            container.css("display", "inherit");
            container.addClass("anchored");

            const anchoredLayerCssPosition: AnchoredLayerCssPosition = {};
            let availableSpace;
            let heightOfAllRows = 0;
            let heightOfContentThatCantFit;

            const browse = getBrowseFromCache(content)!;
            const heightOfRequiredParts = getHeightOfRequiredParts(browse);

            const headerRow = eelPositioningSettings.getHeaderRow();
            const rows = eelPositioningSettings.getRows();

            if (rows.length > 0)
            {
                let minimumHeightInfo: MinimumHeightInfo =
                {
                    rowsFoundForMinHeight: 0,
                    heightOfOneRow: 0,
                    heightOfFoundMinRows: 0
                };

                rows.each(function (this: HTMLElement)
                {
                    const row = $(this);
                    const rowHeight = row.outerHeight(true)!;
                    if (eelPositioningSettings.minRowsToShow && minimumHeightInfo.rowsFoundForMinHeight! < eelPositioningSettings.minRowsToShow)
                    {
                        minimumHeightInfo = getMinimumHeightInfo(rowHeight, minimumHeightInfo);
                    }
                    heightOfAllRows += rowHeight;
                });

                // The minimum body height should be at least 3 rows tall.
                if (eelPositioningSettings.minRowsToShow && minimumHeightInfo.rowsFoundForMinHeight)
                {
                    if (minimumHeightInfo.heightOfOneRow && minimumHeightInfo.rowsFoundForMinHeight > 0 && minimumHeightInfo.rowsFoundForMinHeight < eelPositioningSettings.minRowsToShow)
                    {
                        const rowsNeedingHeight = eelPositioningSettings.minRowsToShow - minimumHeightInfo.rowsFoundForMinHeight;
                        eelPositioningSettings.minContentHeight = minimumHeightInfo.heightOfFoundMinRows!;
                        eelPositioningSettings.minContentHeight += minimumHeightInfo.heightOfOneRow * rowsNeedingHeight;
                    }
                    else if (minimumHeightInfo.rowsFoundForMinHeight !== eelPositioningSettings.minRowsToShow)
                    {
                        const STANDARD_ROW_HEIGHT = 33;
                        eelPositioningSettings.minContentHeight = STANDARD_ROW_HEIGHT * eelPositioningSettings.minRowsToShow;
                    }
                }
            }

            if (heightOfAllRows < eelPositioningSettings.minContentHeight)
            {
                heightOfAllRows = eelPositioningSettings.minContentHeight;
            }
            positionBelowAnchorTo = getPositionBelowAnchorTo();
            if (positionBelowAnchorTo)
            {
                availableSpace = getClientHeight() - (positioningManager.anchorToOffset?.bottom ?? 0) - WINDOW_PADDING;

                let scrollbarPadding = 0;
                if (browse !== undefined && browse !== null && hasHorizontalScrollBar(browse.unlockedBody as JQueryInput))
                {
                    scrollbarPadding = scrollbarHeight;
                }

                const desiredHeightOfCodeEELBrowse = heightOfRequiredParts + heightOfAllRows + scrollbarPadding;
                if (availableSpace > desiredHeightOfCodeEELBrowse)
                {
                    content.height(desiredHeightOfCodeEELBrowse);
                }
                else
                {
                    const widthBeforeResize = content.width();
                    content.height(availableSpace);
                    if (content.width() == widthBeforeResize)
                    {
                        // Some browsers like FireFox add the scrollbar inside the width of content.
                        // The preferred way is Chrome which adds the scrollbar to the content so that the width of content remains the same.
                        content.width(content.width()! + scrollbarHeight);
                    }
                }
                anchoredLayerCssPosition.top = positioningManager.anchorToOffset?.bottom ?? 0;
            }
            else
            {
                availableSpace = positioningManager.anchorToOffset!.top - WINDOW_PADDING;
                const contentHeight = heightOfRequiredParts + heightOfAllRows;

                if (availableSpace >= heightOfRequiredParts + heightOfAllRows)
                {
                    content.height(contentHeight);
                    anchoredLayerCssPosition.top = positioningManager.anchorToOffset!.top - contentHeight;
                }
                else
                {
                    heightOfContentThatCantFit = contentHeight - availableSpace;
                    const adjustedContentHeight = contentHeight - heightOfContentThatCantFit;
                    content.height(adjustedContentHeight);
                    anchoredLayerCssPosition.top = positioningManager.anchorToOffset!.top - adjustedContentHeight;
                }
            }

            const displayLeftOfElement = (getClientWidth() - positioningManager.anchorToOffset!.left) < getClientWidth() / 2;

            const CONTAINER_BORDER = 2;
            const rowWidth = headerRow.outerWidth(true)! + CONTAINER_BORDER;
            let browseWidth;

            if (!rowWidth || rowWidth < eelPositioningSettings.minimumBrowseWidth)
            {
                browseWidth = eelPositioningSettings.minimumBrowseWidth;
            }
            else
            {
                browseWidth = rowWidth;
            }

            if (displayLeftOfElement)
            {
                const availableSpaceLeft = positioningManager.anchorToOffset!.right! - WINDOW_PADDING;
                if (availableSpaceLeft < eelPositioningSettings.minimumBrowseWidth || availableSpaceLeft < rowWidth)
                {
                    content.outerWidth(availableSpaceLeft);
                    anchoredLayerCssPosition.left = WINDOW_PADDING;
                }
                else
                {
                    content.outerWidth(browseWidth);
                    anchoredLayerCssPosition.left = positioningManager.anchorToOffset!.right! - browseWidth;
                }
            }
            else
            {
                const availableSpaceRight = getClientWidth() - positioningManager.anchorToOffset!.left - WINDOW_PADDING;
                if (availableSpaceRight < eelPositioningSettings.minimumBrowseWidth || availableSpaceRight < rowWidth)
                {
                    content.outerWidth(availableSpaceRight);
                }
                else
                {
                    content.outerWidth(Math.ceil(hasVerticalScrollBar(content) ? browseWidth + scrollbarHeight : browseWidth));
                }
                anchoredLayerCssPosition.left = positioningManager.anchorToOffset!.left;
            }
            content.css(anchoredLayerCssPosition as JQuery.PlainObject);

            function getBrowseFromCache(content: JQuery<HTMLElement>)
            {
                if (cache.browseID === undefined)
                {
                    const browse = getInnerBrowse(content)!;
                    if (browse !== undefined)
                    {
                        cache.browseID = browse.id;
                    }
                }
                return getBrowseById(cache.browseID);
            }

            function getHeightOfRequiredParts(browse: Browse)
            {
                let footerHeight = 0;

                if (cache.browseControlsHeight === undefined || cache.browseControlsHeight === null)
                {
                    if (browse !== undefined && browse !== null)
                    {
                        // if browse.controls isn't available, don't try and cache the height
                        if (browse.controls !== undefined && browse.controls !== null)
                        {
                            cache.browseControlsHeight = parseInt(browse.controls.outerHeight(true)?.toString() || "0");
                        }
                    }
                    else
                    {
                        cache.browseControlsHeight = 0;
                    }
                }
                if (cache.browseHeaderHeight === undefined || cache.browseHeaderHeight === null)
                {
                    if (browse !== undefined && browse !== null)
                    {
                        // if browse.controls isn't available, don't try and cache the height
                        if (browse.header !== undefined && browse.header !== null)
                        {
                            cache.browseHeaderHeight = parseInt(browse.header.outerHeight(true)?.toString() || "0");
                        }
                    }
                    else
                    {
                        cache.browseHeaderHeight = 0;
                    }
                }

                if (browse !== undefined && browse !== null && browse.footer !== undefined && browse.footer !== null)
                {
                    // footer will not appear for 0 results, due to variability and the fact that there can be rows that do not appear to the user, like CMA message rows
                    // it is best not to cache this right now without further work to detect both scenarios.
                    footerHeight = parseInt(browse.footer.outerHeight(true)?.toString() || "0");
                }

                const heightOfRequiredParts = (cache.browseControlsHeight || 0) + (cache.browseHeaderHeight || 0) + footerHeight;
                return heightOfRequiredParts;
            }

            function getMinimumHeightInfo(rowHeight: number, minimumHeightInfo: MinimumHeightInfo)
            {
                const newMinimumHeightInfo: MinimumHeightInfo = {};
                if (rowHeight > 0)
                {
                    newMinimumHeightInfo.heightOfFoundMinRows = minimumHeightInfo.heightOfFoundMinRows! += rowHeight;
                    newMinimumHeightInfo.rowsFoundForMinHeight = minimumHeightInfo.rowsFoundForMinHeight! += 1;
                    newMinimumHeightInfo.heightOfOneRow = rowHeight;
                }
                return newMinimumHeightInfo;
            }
        }
    };

    if ("animationPromise" in dialog && dialog.animationPromise)
    {
        dialog.animationPromise.done(function ()
        {
            canSetHeight = true;
        });
    }
    else
    {
        canSetHeight = true;
    }

    return positioningManager;

    function getMinimumModalHeight()
    {
        let minimumModalHeight = minimumContentCssHeight ? minimumContentCssHeight : MINIMUM_CONTENT_OUTER_HEIGHT;
        minimumModalHeight += positioningManager.measurements.containerWithoutContent;
        return minimumModalHeight;
    }

    function setAllowedAnchoredContentHeight(positionBelowAnchorTo: boolean)
    {
        let differenceInHeights: number;
        let originalModalHeightCanFit;
        let availableBottomContentSpace: number | undefined = undefined;
        let availableBottomSpace: number;
        let adjustedContentHeight: number;
        const totalModalHeight = positioningManager.measurements.entireContainer;
        const minimumModalHeight = getMinimumModalHeight();

        if (positionBelowAnchorTo)
        {
            availableBottomSpace = getClientHeight() - (positioningManager.anchorToOffset?.bottom ?? 0) - WINDOW_PADDING;
            originalModalHeightCanFit = totalModalHeight < availableBottomSpace;
            availableBottomContentSpace = availableBottomSpace - positioningManager.measurements.containerWithoutContent;
        }
        else
        {
            originalModalHeightCanFit = totalModalHeight < positioningManager.anchorToOffset!.top;
            differenceInHeights = totalModalHeight - positioningManager.anchorToOffset!.top;
        }

        if (originalModalHeightCanFit)
        {
            positioningManager.measurements.allowedContentHeight = positioningManager.measurements.contentOnly;
        }
        else
        {
            adjustedContentHeight = positionBelowAnchorTo ? availableBottomContentSpace! : positioningManager.measurements.contentOnly - differenceInHeights!;
            adjustedContentHeight -= WINDOW_PADDING;
            positioningManager.measurements.allowedContentHeight = (minimumModalHeight > adjustedContentHeight) ? minimumModalHeight : adjustedContentHeight;
        }
    }

    function setAnchorToOffset()
    {
        positioningManager.anchorToOffset = anchorTo.offset();
        positioningManager.anchorToOffset!.right = positioningManager.anchorToOffset!.left + anchorTo.outerWidth()!;
        positioningManager.anchorToOffset!.bottom = positioningManager.anchorToOffset!.top + anchorTo.outerHeight()!;
    }

    function getPositionBelowAnchorTo()
    {
        if (positioningSettings.maintainRelativePosition !== true)
        {
            return calculatePositionBelowAnchorTo();
        }

        if (cache.positionBelowAnchorTo === undefined)
        {
            cache.positionBelowAnchorTo = calculatePositionBelowAnchorTo();
        }

        return cache.positionBelowAnchorTo;

        function calculatePositionBelowAnchorTo()
        {
            const spaceBelowAnchorTo = getClientHeight() - (positioningManager.anchorToOffset?.bottom ?? 0);
            const spaceAboveAnchorTo = positioningManager.anchorToOffset!.top;

            return spaceBelowAnchorTo >= spaceAboveAnchorTo;
        }
    }

    function setMeasurements()
    {
        const originalHeight = content.height()!;
        const originalScrollTop = content.scrollTop()!;

        content.height("");

        positioningManager.measurements.contentOnly = Math.ceil(content.outerHeight(true)!);

        // sum up the height of all non content parts since the modal container is positioned absolutely
        positioningManager.measurements.containerWithoutContent = 0;
        container.contents().not(".modal.content").each(function ()
        {
            positioningManager.measurements.containerWithoutContent += $(this).outerHeight(true)!;
        });

        const containerWidth = container.outerWidth(true)!;
        const contentWidth = content.outerWidth(true)!;

        positioningManager.measurements.containerOuterWidth = containerWidth > contentWidth ? containerWidth : contentWidth;
        positioningManager.measurements.entireContainer = positioningManager.measurements.contentOnly + positioningManager.measurements.containerWithoutContent;

        content.height(originalHeight);
        content.scrollTop(originalScrollTop);
    }

    function positionUnanchoredLayer()
    {
        dialog.container.css('top', '');
        dialog.container.css('left', '');
        dialog.container.css('position', 'relative');
        if ("content" in dialog && dialog.content)
        {
            dialog.content.css('height', '');
        }
    }

    function positionAnchoredLayer()
    {
        setAnchorToOffset();

        container.css("display", "inherit");
        container.addClass("anchored");
        dialog.container.css('position', '');

        let displayAboveElement = false;
        let displayLeftOfElement = false;
        const ARROW_MIDDLE_OFFSET = 14; // 14 = half of arrow width (28)
        const ARROW_INDENT_PERCENT = 5; // % in from the left or right where we start the arrow
        const anchorToWidth = anchorTo.outerWidth()!;
        const containerOuterWidth = container.outerWidth(true)!;
        const arrowPosition = (containerOuterWidth * (ARROW_INDENT_PERCENT / 100)) + ARROW_MIDDLE_OFFSET;
        const MINIMUM_SPACING_FROM_EDGE_OF_SCREEN = 10; // we don't want an anchored dialog touching the edge of the screen

        position();

        function removeOldArrowPositionClasses()
        {
            container.removeClass("topLeft topRight bottomLeft bottomRight");
        }

        function getArrowPositionClass()
        {
            const arrowCSSPositionClasses = ["topLeft", "topRight", "bottomLeft", "bottomRight"];
            if (displayAboveElement && displayLeftOfElement)
            {
                return arrowCSSPositionClasses[3];
            }
            else if (displayAboveElement && !displayLeftOfElement)
            {
                return arrowCSSPositionClasses[2];
            }
            else if (!displayAboveElement && displayLeftOfElement)
            {
                return arrowCSSPositionClasses[1];
            }
            return arrowCSSPositionClasses[0];
        }

        function position()
        {
            const anchoredLayerCssPosition: AnchoredLayerCssPosition = {};

            const scrollTop = document.documentElement.scrollTop;
            const scrollLeft = document.documentElement.scrollLeft;

            // because the anchorTo is not visible to the user, we run through this unanchored position function and return
            if (positioningManager.anchorToOffset!.left - scrollLeft >= getClientWidth() || positioningManager.anchorToOffset!.left < 0 ||
                positioningManager.anchorToOffset!.top - scrollTop >= getClientHeight() || positioningManager.anchorToOffset!.top < 0 ||
                positioningManager.anchorToOffset!.left - scrollLeft + arrowPosition + MINIMUM_SPACING_FROM_EDGE_OF_SCREEN > getClientWidth()
                    // We don't check against the left side of the screen because row-level browse buttons can get very close to the edge.
                    // We'd rather the arrow be slightly off center, rather than in the middle of the screen.
                    /* || positioningManager.anchorToOffset.left - arrowPosition - minimumSpacingFromEdgeOfScreen < 0 */)
            {
                renderAnchoredAsUnanchored();
                return;
            }

            setMeasurements();
            positionBelowAnchorTo = getPositionBelowAnchorTo();
            setAllowedAnchoredContentHeight(positionBelowAnchorTo);

            if (positionBelowAnchorTo)
            {
                anchoredLayerCssPosition.top = (positioningManager.anchorToOffset?.bottom ?? 0) + ANCHORED_TAIL_HEIGHT;
            }
            else
            {
                displayAboveElement = true;
                anchoredLayerCssPosition.top = positioningManager.anchorToOffset!.top - (positioningManager.measurements.containerWithoutContent + positioningManager.measurements.allowedContentHeight + ANCHORED_TAIL_HEIGHT);
            }
            positioningManager.measurements.top = anchoredLayerCssPosition.top;

            if (canSetHeight)
            {
                content.outerHeight(positioningManager.measurements.allowedContentHeight || 'auto');
            }

            if (getClientWidth() < positioningManager.anchorToOffset!.left + containerOuterWidth)
            {
                if (positioningManager.anchorToOffset!.left < containerOuterWidth)
                {
                    renderAnchoredAsUnanchored();
                    return;
                }
                displayLeftOfElement = true;
                const CLIENT_WIDTH_PADDING = 2;
                const distanceFromRight = (getClientWidth() - CLIENT_WIDTH_PADDING) - (positioningManager.anchorToOffset!.left + (anchorToWidth / 2)) - arrowPosition;
                anchoredLayerCssPosition.left = getClientWidth() - distanceFromRight - containerOuterWidth;
            }
            else
            {
                const MODULE_CONTENT_PADDING = 10;
                displayLeftOfElement = false;
                let anchoredLayerPositionLeft = positioningManager.anchorToOffset!.left + (anchorToWidth / 2) - arrowPosition;

                if (anchoredLayerPositionLeft < MODULE_CONTENT_PADDING)
                {
                    anchoredLayerPositionLeft = MODULE_CONTENT_PADDING;

                    const arrowOffset = positioningManager.anchorToOffset!.left + (anchorToWidth / 2) - anchoredLayerPositionLeft - ARROW_MIDDLE_OFFSET;

                    if (!container.hasClass('customArrowPosition'))
                    {
                        $("<style type='text/css'> #" + container[0].id + ":before, #" + container[0].id + ":after { left: " + arrowOffset + "px; } </style>").appendTo(".modalLayerContainer");
                        container.addClass('customArrowPosition');
                    }
                }

                const positionLeftAndContainerWidth = anchoredLayerPositionLeft + containerOuterWidth;
                if (positionLeftAndContainerWidth < getClientWidth())
                {
                    anchoredLayerCssPosition.left = anchoredLayerPositionLeft;
                }
                else
                {
                    const overlapValue = positionLeftAndContainerWidth - getClientWidth();
                    anchoredLayerCssPosition.left = anchoredLayerPositionLeft - overlapValue - WINDOW_PADDING;
                }
            }

            container.css(anchoredLayerCssPosition as JQuery.PlainObject);

            const dialogArrowClass = getArrowPositionClass();
            removeOldArrowPositionClasses();
            container.addClass(dialogArrowClass);
        }

        function renderAnchoredAsUnanchored()
        {
            removeOldArrowPositionClasses();
            positionUnanchoredLayer();
        }
    }
}

/**
 * Return true of the passed in element/selector is found within the ModalLayer
 * @param selector element/selector to try to find within the current ModalLayer
 * @returns Was the element found within the ModalLayer
 */
export function elementIsInModalLayer(selector: JQueryInput)
{
    const element = $(selector);

    return element.closest(".modalLayerContainer").length > 0;
}

export function buildRecommendationModalContent(message?: string, recommendations?: string[], isWarning: boolean = false, orderedList: boolean = false)
{
    const content = $("<div/>");

    if (isWarning)
    {
        $("<p/>", { class: "bold", text: "WARNING:" }).appendTo(content);
        $("<br>").appendTo(content);
    }

    $("<div/>",
        {
            class: "recommendationDialog__text",
            text: message
        }).appendTo(content);

    $("<br>").appendTo(content);

    if (recommendations && recommendations.length > 0)
    {
        $("<p/>",
            {
                class: "bold",
                text: "RECOMMENDATIONS:"
            }).appendTo(content);

        $("<br>").appendTo(content);

        const recommendationsList = $(orderedList ? "<ol/>" : "<ul/>",
            { class: "recommendationDialog__list" })
            .appendTo(content);

        recommendations.forEach((recommendation) =>
        {
            $("<li/>",
                {
                    class: "recommendationDialog__text",
                    text: recommendation
                }).appendTo(recommendationsList);
        });
    }
    return content;
}

function concatAdditionalClasses(object: ModalDialogSettings | undefined, value: string)
{
    object ??= {};
    object.additionalClasses = (`${object.additionalClasses ?? ""} ${value}`).trim();
    return object;
}

function showMobileEelDialog(popupContent: JQueryInput, _textbox: JQuery<HTMLElement>, containerToExposeSelector: JQueryInput, beforeClose: BeforeCloseFunction, eelData: CodeEELData, bindings?: Array<any>)
{
    const containerToExpose = $(containerToExposeSelector);
    if (containerToExpose.is(':animated'))
    {
        return;
    }

    let mobileEelParts: MobileEelParts;
    const ANIMATION_DURATION = 300;
    const isDMSE = containerToExpose.closest(".distinctMultiSelectEEL").addBack(".distinctMultiSelectEEL").length === 1;

    const adjustHeaderMarginForDMSE = debounce(function ()
    {
        mobileEelParts.modal.element!.find('.modalHeader').css('margin-bottom', 'calc(' + mobileEelParts.textbox.element.outerHeight() + 'px + 1.5rem)');
    }, 10);

    initEelLayer();

    showEelLayer();

    return eelData.layer;

    function initEelLayer()
    {
        const modalLayerOptions: ModalLayerOptions =
        {
            bindings: bindings,
            expose: [containerToExpose],
            tailessAnchored: true,
            afterClose: afterLayerClose,
            resizeCallback: function ()
            {
                if (isDMSE)
                {
                    adjustHeaderMarginForDMSE();
                }
            }
        };

        const textboxOffsets = containerToExpose.offset()!;
        const textboxWidth = containerToExpose.css('width');

        mobileEelParts =
        {
            modal:
            {
                origPosition: { opacity: "0" },
                newPosition: { opacity: "1" },
                skipAfterClose: true
            },
            textbox:
            {
                element: containerToExpose,
                origStyle: (containerToExpose.attr('style') || ""),
                placeholder: containerToExpose.clone(),
                afterShowAnimate: function ()
                {
                    if (isDMSE)
                    {
                        mobileEelParts.textbox.element.css('width', 'calc(100% - 7.75rem)');
                        adjustHeaderMarginForDMSE();
                    }
                },
                origPosition:
                {
                    position: 'fixed',
                    top: textboxOffsets.top.toString(),
                    left: textboxOffsets.left.toString(),
                    width: textboxWidth,
                    margin: "0"
                },
                newPosition:
                {
                    position: 'fixed',
                    top: '4.7rem',
                    left: '0.7rem',
                    width: textboxWidth,
                    margin: "0"
                }
            }
        };

        eelData.layer = newModalLayer(popupContent, function (layer, reason)
        {
            if (typeof beforeClose === 'function')
            {
                return (beforeClose as (layer: ModalLayer, reason?: CloseReason) => void | boolean)(layer, reason);
            }
        }, modalLayerOptions);
        mobileEelParts.modal.element = eelData.layer.container.addClass('modalLayer-mobile-eel');

        if (isDMSE)
        {
            eelData.layer.container.addClass('modalLayer-mobile-eel-dmse');
        }

        addModalHeading();
    }

    function showEelLayer()
    {
        doBeforeShow();

        // Iterate over keys of mobileEelParts with proper typing
        (Object.keys(mobileEelParts) as Array<keyof MobileEelParts>).forEach((key) =>
        {
            const part = mobileEelParts[key];

            if ('placeholder' in part && part.placeholder)
            {
                part.placeholder.addClass('mobile-eel-placeholder').insertAfter(part.element);
            }

            part.element!
                .css(part.origPosition as any)
                .stop()
                .animate(part.newPosition, ANIMATION_DURATION, null as any, () =>
                {
                    if ('afterShowAnimate' in part && typeof part.afterShowAnimate === 'function')
                    {
                        part.afterShowAnimate();
                    }
                });
        });

        if (isIosApp)
        {
            $(document.scrollingElement!).animate({ scrollTop: 0 }, ANIMATION_DURATION);
        }

        eelData.layer?.show();
    }

    function addModalHeading()
    {
        $("<div>", { 'class': 'title modalHeader' }).append($('<h1>', { 'class': 'modalHeaderText', 'html': getEelLabel() })).prependTo(mobileEelParts.modal.element!.find('.modalLayer'));

        if (isDMSE)
        {
            adjustHeaderMarginForDMSE();
        }
    }

    function getEelLabel()
    {
        let label = "";
        const controlLabel = containerToExpose.attr('data-validate-control-for');
        if (controlLabel)
        {
            const controlLabelElement = containerToExpose.closest('.fieldWrapper').find('[data-validate-text-for=' + controlLabel.replace(/\./g, '\\.') + ']');
            if (controlLabelElement.length)
            {
                label = controlLabelElement.text().replace(/\*/g, '');
            }
        }

        if (label.indexOf('Select') != 0)
        {
            label = 'Select ' + label;
        }

        return label;
    }

    function closeEelLayer(event: JQuery.TriggeredEvent)
    {
        (event.data.layer as ModalLayer).glass.trigger('click');
    }

    function doBeforeShow()
    {
        mobileEelParts.modal.element!.css({ 'top': '0', 'bottom': '0', 'height': 'auto' });
        $(document.body).addClass('mobileEelOpen');
        //@ts-ignore skyward.mobile.base.js doesn't have a.d.ts file
        skyward.mobile.scrollToTop();
    }

    function doAfterClose()
    {
        $(document.body)
            .off('click', '.distinctMultiSelectSearchButton', closeEelLayer)
            .removeClass('mobileEelOpen');
    }

    function afterLayerClose()
    {
        const debouncedAfterClose = debounce(doAfterClose, 10);
        const elementData = new WeakMap<Element, MobileEelParts[keyof MobileEelParts]>();

        (Object.keys(mobileEelParts) as Array<keyof MobileEelParts>).forEach((key) =>
        {
            const part = mobileEelParts[key];

            if ('skipAfterClose' in part && part.skipAfterClose)
            {
                return;
            }

            elementData.set(part.element![0], part);

            part.element!
                .css(part.newPosition as any)
                .stop()
                .animate(part.origPosition, ANIMATION_DURATION, function ()
                {
                    const eelPart = elementData.get(this);
                    if (eelPart)
                    {
                        if ('placeholder' in eelPart && eelPart.placeholder)
                        {
                            eelPart.placeholder.remove();
                        }

                        if ('origStyle' in eelPart)
                        {
                            eelPart.element!.attr('style', eelPart.origStyle);
                        }

                    }
                    debouncedAfterClose();
                });
        });
    }
}

function preventScrollingOnExposedElements(e: JQuery.TriggeredEvent)
{
    e.preventDefault();
    return false;
}

interface ModalLayerDebugData
{
    layerid?: number;
    _modalLayerDebug?: {
        name: string | null;
        creator: string;
    } | null;
}

registerErrorDataGatherer("modallayer", function ()
{
    const layers: { name?: string | null; creator?: string; }[] = [];

    $(".modalLayerContainer").toArray().forEach(function (containerElem)
    {
        const data: ModalLayerDebugData = $(containerElem).data() || {};
        if (data.layerid === undefined)
        {
            return;
        }

        layers[data.layerid] = data._modalLayerDebug || {};
    });

    return layers;
});

function createModalMenu(menuItems: MenuItemDefinition[], anchorTo: JQueryInput, options?: ModalMenuSettings)
{
    const settings = $.extend({}, getDefaultMenuSettings(), options) as ModalMenuSettings;
    const menu: Partial<ModalMenu> =
    {
        container: $("<div>", { 'class': 'menuPopup' }),
        content: $("<div>", { 'class': 'menuPopup modal content' }),
        close: function ()
        {
            menu.modalLayer!.options.forceClose = true;
            menu.modalLayer!.close();
            menu.closed = true;
            return true;
        },
        closed: false
    };
    anchorTo = $(anchorTo);

    menu.container!.append(menu.content!);

    const menuElements = menuItems.map(menuItem => createMenuItem(menuItem, menu.close!, menu as ModalMenu)).filter((el): el is JQuery<HTMLElement> => el !== null);;
    menu.content!.append(menuElements);

    if (menuItems.length > 0 && menuItems[0].type === "title")
    {
        menu.container!.attr("aria-labelledby", menuElements[0].addIds().attr("id")!);
    }

    const positioningManager = createModalPositioningManager(menu as ModalMenu, anchorTo, menu.content!);

    menu.modalLayer = newModalLayer(menu.container!, settings.beforeClose,
        {
            afterClose: settings.afterClose,
            resizeCallback: function ()
            {
                positioningManager.positionModalContents();
                setMenuWidth(menu.container!);
            },
            debugName: settings.debugName,
            role: "menu"
        });

    positioningManager.positionModalContents();
    setMenuWidth(menu.container!);
    menu.modalLayer.show();
    return menu as ModalMenu;
}

function createMenuItem(menuItemDefinition: MenuItemDefinition, closeFunction: (reason?: string) => boolean, menu: ModalMenu)
{
    if (menuItemDefinition == null)
    {
        return null;
    }

    menuItemDefinition = $.extend({}, getDefaultMenuItemSettings(), menuItemDefinition);

    if (menuItemDefinition.type?.toLowerCase() === "custom")
    {
        if (menuItemDefinition.content == null)
        {
            return null;
        }
        if (typeof menuItemDefinition.content === "function")
        {
            return menuItemDefinition.content();
        }
        return menuItemDefinition.content;
    }

    const isTitle = menuItemDefinition.type?.toLowerCase() == "title";
    const iconSpan = $("<span>", { 'class': "menuPopupItemIcon", "aria-hidden": true });
    const textSpan = $(isTitle ? "<h1>" : "<span>", { 'class': "anchorText textAfter svgGroup" }).text(menuItemDefinition.text!);
    const svg = getSVG(menuItemDefinition.icon as string | JQueryInput | null, "MessageBox");
    svg?.addClass("svgNoHover");

    iconSpan.append(svg!);

    let defaultHasPopup;
    if (menuItemDefinition.target === "popup")
    {
        defaultHasPopup = "dialog";
    }
    else
    {
        defaultHasPopup = "false";
    }
    const anchorAttributes: Dictionary<string, any> =
    {
        id: menuItemDefinition.id,
        href: "javascript:void(0);",
        'class': isTitle ? "menuPopupTitle" : "menuPopupItem",
        role: isTitle ? undefined : "menuitem",
        "aria-haspopup": menuItemDefinition.hasPopup || defaultHasPopup,
    };

    if (menuItemDefinition.selected)
    {
        anchorAttributes['class'] += " selected";
    }

    if (menuItemDefinition.attributes)
    {
        for (const attributeName in menuItemDefinition.attributes)
        {
            anchorAttributes[attributeName] = menuItemDefinition.attributes[attributeName];
        }
    }

    const menuItem = isTitle ? $("<div>", anchorAttributes) : $("<a>", anchorAttributes);
    menuItem.append(iconSpan, textSpan);

    if (menuItemDefinition.disabled)
    {
        menuItem.addClass("ui-disabled");
    }

    if (menuItemDefinition.data != null)
    {
        menuItem.data(menuItemDefinition.data);
    }

    setClickEvent(menuItem, menuItemDefinition.click, closeFunction, menu);

    menuItem.on("click", function ()
    {
        if (!menuItem.prop("disabled") && !menuItem.hasClass("ui-disabled"))
        {
            closeFunction();
        }
    });

    return menuItem;
}

function setMenuWidth(menu: JQuery<HTMLElement>)
{
    const content = menu.find('.content');
    let greatestChildWidth = 0;
    menu.children().each(function ()
    {
        const menuContent = $(this);
        menuContent.width(''); // reset content width so we don't grow for the scrollbar each time
        const childWidth = menuContent.outerWidth(true)! + 1;  // Add one to compensate for IE rounding error on outerWidth
        if (childWidth > greatestChildWidth)
        {
            greatestChildWidth = childWidth;
        }
    });

    if (Math.ceil(content.height()!) != content[0].scrollHeight)
    {
        greatestChildWidth += 20; // Accommodate for scroll bar
    }
    content.width(greatestChildWidth);
}

function createModalDialog(title: string, contents: ModalContents, buttonDefinitions?: ButtonDefinitions | null, options?: ModalDialogSettings)
{
    const settings = $.extend({}, getDefaultModalDialogSettings(), options);

    if (buttonDefinitions)
    {
        $.each(buttonDefinitions, function (_index, button: ButtonDefinition)
        {
            if (button?.text === ButtonText.Save)
            {
                settings.closeOnClick = false;
                return false;
            }
            return true;
        });
    }

    if (settings.anchorTo && !(settings.anchorTo instanceof $))
    {
        settings.anchorTo = $(settings.anchorTo);
    }

    const deferredAnimation = $.Deferred();
    const publicBodyDeferred = $.Deferred();
    const deferredDynamicContentLoaded = $.Deferred();

    const dialog: Partial<ModalDialog> =
    {
        title: $("<div>", { class: ("messageBox title modalHeader " + settings.additionalClasses).trim() }).append($(`<${settings.titleTag}>`, { class: "modalHeaderText", "html": title })).addIds(),
        content: $("<div>", { class: "messageBox modal content" }).addIds(),
        footer: $("<div>", { class: ("modalContentFooter " + settings.additionalClasses).trim() }).addIds(),
        close: closeDialog,
        animateModalContents,
        closed: false,
        animationPromise: deferredAnimation.promise(),
        bodyPromise: publicBodyDeferred.promise(),
        dynamicContentLoadedPromise: deferredDynamicContentLoaded.promise(),
    };

    dialog.container = $("<div>",
        {
            id: settings.id,
            class: "messageBox skywardContentPanel skywardSizedContainer",
            "data-automation-id": settings.automationID,
            "aria-labelledby": dialog.title!.attr("id"),
            "aria-modal": true,
            append: [dialog.title, dialog.content, dialog.footer]
        });

    if (settings.role == "alertdialog")
    {
        dialog.container.attr("aria-describedby", dialog.content!.attr("id")!);
    }

    if (settings.hideOverflow)
    {
        dialog.content!.css('overflow', 'hidden');
    }

    if (settings.useSmallWidth)
    {
        dialog.container.addClass("smallWidth");
        dialog.content!.addClass("contentSmallWidth");
    }

    const positioningManager = createModalPositioningManager(dialog as ModalDialog, settings.anchorTo!, dialog.content!);

    const populateButtonsPromise = populateButtons(buttonDefinitions, dialog.close!, settings, dialog as ModalDialog);
    $.when(populateButtonsPromise).then(function ()
    {
        if ((mobileAccess || settings.useMobileAccessStyles) && !dialog.footer!.children('button').length)
        {
            dialog.footer!.remove();
        }
    });

    dialog.modalLayer = newModalLayer(dialog.container, settings.beforeClose,
        {
            afterClose: settings.afterClose,
            closeOnClick: settings.closeOnClick,
            closeOnEscape: settings.closeOnEscape,
            forceClose: settings.forceClose,
            transparent: settings.transparentGlass,
            resizeCallback: positioningManager.positionModalContents,
            loadDOMCallback: settings.dynamicDialogContentSelector ? animateAfterLoadDOMCallback : false,
            closeOnRecordSelected: settings.closeOnRecordSelected,
            expose: settings.expose,
            role: settings.role,
            initialFocusElement: settings.initialFocusElement,
            preserveFocus: settings.preserveFocus,
            additionalContainerClasses: settings.additionalContainerClasses
        });

    dialog.container.css('display', 'none');

    const bodyPromise = populateBody(dialog.content!, contents);

    $.when(bodyPromise).then(function ()
    {
        // in case of slow network, the user can click the glass before the modal appears, closing it before it appears.
        // no need to do any showing in this case
        if (!dialog.modalLayer?.closing)
        {
            if (settings.beforeShowContentPromise)
            {
                $.when(settings.beforeShowContentPromise).then(function ()
                {
                    contentLoadedSuccess();
                });
            }
            else
            {
                contentLoadedSuccess();
            }

            if (!settings.dynamicDialogContentSelector)
            {
                dialog.content!.css('visibility', 'visible'); // we need to have content take up space so fieldset.ts can lineup Field Wrappers
                dialog.animateModalContents!();
            }

            dialog.content!.on('treeBrowseContentLoaded', function ()
            {
                dialog.animateModalContents!();
            });

            // Don't set initial focus in mobile mode, it will display the onscreen keyboard
            if (!mobileAccess)
            {
                publicBodyDeferred.then(function ()
                {
                    //@ts-ignore skyward.ui.focusManager.ts doesn't have a .d.ts file
                    skyward.dialogFocusManager.setFocus(dialog.modalLayer);
                });
            }
        }

        publicBodyDeferred.resolve();

    }, function ()
    {
        dialog.close!();
    });

    if (settings.draggable && !mobileAccess)
    {
        makeDraggable(dialog.container, { handle: ".modalHeader.title" });
    }

    $.when(populateButtonsPromise, bodyPromise).done(initLayerPseudoForm);

    // a registerLoadDOMCallback is necessary to account for content added after the dialog loads, such as browses.
    registerLoadDOMCallback(checkInitLayerPseudoForm);

    if (settings.width)
    {
        dialog.container.css('width', settings.width);
    }

    if (settings.height)
    {
        dialog.container.css('height', settings.height);
    }

    return dialog as ModalDialog;

    function animateModalContents()
    {
        positioningManager.positionModalContents();
        if (positioningManager.isAnchored())
        {
            dialog.content!.outerHeight(positioningManager.measurements.allowedContentHeight || 'auto');
        }
        deferredAnimation.resolve();
        return deferredAnimation.promise();
    }

    function closeDialog(reason?: string)
    {
        const closed = dialog.modalLayer!.close(reason);
        if (!closed)
        {
            return false;
        }

        dialog.closed = true;
        removeLoadDOMCallback(checkInitLayerPseudoForm);
        return true;
    };

    function contentLoadedSuccess()
    {
        dialog.modalLayer!.show();
        dialog.container!.css('display', '');

        dialog.content!.trigger("contentLoaded", dialog);

        if (!dialog.container!.attr('id'))
        {
            dialog.container!.attr('id', 'modalLayerDialog' + dialog.modalLayer!.uniqueId);
        }

        const containerId = '#' + dialog.container!.attr('id');
        notifyDOMLoad(containerId + ' > .content');
        if (!mobileAccess)
        {
            if (settings.dynamicDialogContentSelector)
            {
                dialog.dynamicContentLoadedPromise?.done(() =>
                {
                    focusDialogAndSetDescription(dialog as ModalDialog);
                });
            }
            else
            {
                focusDialogAndSetDescription(dialog as ModalDialog);
            }
        }
    }

    function animateAfterLoadDOMCallback(selector: string)
    {
        if (selector === settings.dynamicDialogContentSelector)
        {
            dialog.animateModalContents!().then(function ()
            {
                deferredDynamicContentLoaded.resolve();
                dialog.content!.show();
                notifyResize();
            });
        }
    }

    function checkInitLayerPseudoForm(this: JQueryInput)
    {
        if (!dialog || !dialog.modalLayer?.onscreen)
        {
            removeLoadDOMCallback(checkInitLayerPseudoForm);
            return;
        }
        const loadedElement = $(this).get(0);
        if (loadedElement && dialog.container!.length && dialog.container![0].contains(loadedElement))
        {
            if (initLayerPseudoForm() !== false)
            {
                removeLoadDOMCallback(checkInitLayerPseudoForm);
            }
        }
    }

    function initLayerPseudoForm()
    {
        if (!dialog || !dialog.modalLayer?.onscreen)
        {
            return;
        }

        let hasSaveButton;
        if (dialog.content!.hasClass("noFooter"))
        {
            hasSaveButton = dialog.content!.find(".save").length > 0;
        }
        else
        {
            hasSaveButton = dialog.footer!.find(".save").length > 0;
        }

        if (hasSaveButton && dialog.content!.find(":input").not("[type=hidden]").length)
        {
            const layerPseudoForm = dialog.content!.closest(".layerPseudoForm");
            const titleId = dialog.title!.find(".modalHeaderText").addIds().attr("id")!;
            layerPseudoForm.attr("role", "form");
            layerPseudoForm.attr("aria-labelledby", titleId);
            return true;
        }
        return false;
    }
}

function populateBody(content: JQuery<HTMLElement>, contents: ModalContents)
{
    if (!contents)
    {
        return $.Deferred().resolve().promise();
    }

    if (typeof contents === "function")
    {
        const promise = contents() as unknown as Promise<any>;
        return appendDeferred(promise);
    }
    // @ts-ignore Not sure how to check if the object is a Promise or JQuery Promise
    else if (typeof contents !== "string" && typeof contents.then === "function")
    {
        return appendDeferred(contents as unknown as Promise<any>);
    }
    else
    {
        if (typeof contents === "string")
        {
            const contentsParts = contents.split("<textarea");
            let newString = "";
            newString = contentsParts[0].replace(/\n/gi, "<br />");
            for (let i = 1; i < contentsParts.length; i++)
            {
                const subContentsParts = contentsParts[i].split("</textarea>");
                newString += "<textarea" + subContentsParts[0];
                if (subContentsParts.length > 1)
                {
                    newString += "</textarea>" + subContentsParts[1].replace(/\n/gi, "<br />");
                }
            }
            contents = newString;
        }
        // Some callers assume strings or jquery elements that are passed in will be inserted into the dom immediately,
        // before newModalLayer() returns.  This is the best we can do without loading scripts synchronously.
        const contentParts = processViewPartHtmlSeparately(contents as string | HTMLElement);
        content.append(contentParts.html);
        return contentParts.loaded;
    }

    function appendDeferred(unwrappedContents: Promise<any>)
    {
        return $.when(unwrappedContents)
            .then(processViewPartHtml)
            .then(function (functionResult)
            {
                content.append(functionResult);
            });
    }
}

function populateButtons(buttonDefinitions: ButtonDefinitions | undefined | null, closeFunction: (reason?: string) => boolean, settings: ModalDialogSettings, dialog: ModalDialog)
{
    const footer = dialog.footer;
    const header = dialog.title;

    return $.when(getButtonsFromDefinitions(buttonDefinitions, closeFunction, settings, dialog)).done(function (buttons)
    {
        if (!buttons)
        {
            return false;
        }

        if (buttons && buttons.mobileClose)
        {
            header.addClass("hasClose");
            header.append(buttons.mobileClose);
        }

        $(buttons.left).each(function ()
        {
            footer.append($(this));
        });

        footer.append($("<div>", { 'class': "modalContentFooter__leftRightButtonGroupSeparator" }));

        $(buttons.right).each(function ()
        {
            footer.append($(this));
        });

        return true;
    });
}

function getButtonsFromDefinitions(definitions: ButtonDefinitions | undefined | null, closeFunction: (reason?: any) => void, settings: ModalDialogSettings, dialog: ModalDialog)
{
    const deferred = $.Deferred();

    const buttonDefinitions: ButtonDefinition[] = [];

    if ((mobileAccess || settings.useMobileAccessStyles) && !definitions)
    {
        buttonDefinitions.push({ text: ButtonText.Close });
    }

    if (definitions)
    {
        if (typeof definitions === "function")
        {
            $.when(definitions()).done(function (functionResult)
            {
                buttonDefinitions.push(...functionResult);
                deferred.resolve(createButtons(buttonDefinitions, closeFunction, settings, dialog));
            });
            return deferred.promise();
        }
        else if (Array.isArray(definitions))
        {
            buttonDefinitions.push(...definitions);
        }
        else
        {
            buttonDefinitions.push(definitions);
        }
    }

    deferred.resolve(createButtons(buttonDefinitions, closeFunction, settings, dialog));

    return deferred.promise();
}

const closeButtonLabels = [ButtonText.Close, ButtonText.Cancel, ButtonText.Done, ButtonText.Ok] as string[];

function createButtons(buttonDefinitions: ButtonDefinition[], closeFunction: (reason?: any) => void, settings: ModalDialogSettings, dialog: ModalDialog)
{
    const buttons: ButtonGroups =
    {
        right: [],
        left: []
    };

    let noVisibleButtons = true;

    const validButtonDefinitions = validateButtonDefinitions(buttonDefinitions, settings);

    if (mobileAccess || settings.useMobileAccessStyles)
    {
        for (let i = validButtonDefinitions.length - 1; i >= 0; i--)
        {
            const buttonDefinition = validButtonDefinitions[i];
            if (closeButtonLabels.includes(buttonDefinition.text))
            {
                createHeaderCloseButton(buttonDefinition);
                validButtonDefinitions.splice(i, 1);
                break;
            }
        }
    }

    for (const definition of validButtonDefinitions)
    {
        if (definition)
        {
            const button = createButton(definition, closeFunction, settings, dialog, false);

            if (button !== null)
            {
                noVisibleButtons = false;
                if (button.hasClass('js--modalContentFooter__button--left'))
                {
                    buttons.left.push(button);
                }
                else
                {
                    buttons.right.push(button);
                }
            }
        }
    }

    // Push on a default ok button, if none were passed in
    if (noVisibleButtons && !settings.allowNoButtons)
    {
        if (mobileAccess || settings.useMobileAccessStyles)
        {
            createHeaderCloseButton();
        }
        else
        {
            const newButton = createButton({}, closeFunction, undefined, undefined, false);

            if (newButton)
            {
                buttons.right.push(newButton);
            }
        }
    }

    function createHeaderCloseButton(buttonDefinition?: ButtonDefinition)
    {
        const button = createButton({ ...buttonDefinition, text: ButtonText.Close, icon: ButtonIcon.Close }, closeFunction, settings, dialog, true);

        if (button)
        {
            noVisibleButtons = false;
            buttons.mobileClose = button;
        }
    }

    return buttons;
}

/**
 * Update button definitions to follow a set of rules to comply with accessibility guidelines:
 * - If there is a "Save" button and a "Close" button but no "Cancel" button, the "Close" button is updated to a "Cancel" button.
 * - If there is ONLY a "Cancel" button, the button is updated to a "Close" button.
 * - If there is ONLY an "Ok" button AND the button is not part of a confirmation step, the button is updated to a "Close" button.
 * @returns the validated and updated button definitions
 */
function validateButtonDefinitions(buttonDefinitions: ButtonDefinition[], settings: ModalDialogSettings)
{
    let saveButton: ButtonDefinition | undefined;
    let cancelButton: ButtonDefinition | undefined;
    let closeButton: ButtonDefinition | undefined;
    let okButton: ButtonDefinition | undefined;

    const processedButtonDefinitions: ButtonDefinition[] = [];

    // This should not be unnecessary, but JS files can pass in things besides ButtonDefinitions
    const unsafeButtonDefinitions = buttonDefinitions as (ButtonDefinition | string | null)[];

    for (const unsafeButtonDefinition of unsafeButtonDefinitions)
    {
        if (typeof unsafeButtonDefinition === "string")
        {
            if (unsafeButtonDefinition.toLowerCase() === ButtonText.Close.toLowerCase())
            {
                processedButtonDefinitions.push(unsafeButtonDefinition as unknown as ButtonDefinition);
            }
            continue;
        }
        else if (!(unsafeButtonDefinition?.text))
        {
            continue;
        }

        const buttonDefinition = { ...unsafeButtonDefinition };
        processedButtonDefinitions.push(buttonDefinition);

        if (buttonDefinition.text.startsWith(ButtonText.Save))
        {
            saveButton = buttonDefinition;
            continue;
        }

        switch (buttonDefinition.text as ButtonText)
        {
            case ButtonText.Cancel:
                cancelButton = buttonDefinition;
                break;
            case ButtonText.Close:
                closeButton = buttonDefinition;
                break;
            case ButtonText.Ok:
                okButton = buttonDefinition;
                break;
        }
    }

    if (saveButton && closeButton && !cancelButton)
    {
        closeButton.text = ButtonText.Cancel;
        closeButton.icon = ButtonIcon.Cancel;
    }
    else if (cancelButton && buttonDefinitions.length === 1)
    {
        cancelButton.text = ButtonText.Close;
        cancelButton.icon = ButtonIcon.Close;
    }
    else if (okButton && buttonDefinitions.length === 1 && !settings.isConfirmationStep)
    {
        okButton.text = ButtonText.Close;
        okButton.icon = ButtonIcon.Close;
    }

    return processedButtonDefinitions;
}

function createButton(buttonDefinition: Partial<ButtonDefinition> | null, closeFunction: (reason?: string) => void, settings?: ModalDialogSettings, dialog?: ModalDialog, isCloseButton?: boolean)
{
    if (buttonDefinition == null)
    {
        return null;
    }

    buttonDefinition = $.extend(true, {}, closeButton, buttonDefinition) as ButtonDefinition;

    const buttonAttributes =
    {
        id: buttonDefinition.id,
        class: (isCloseButton ? "informationDialogClose " : "modalContentFooter__button ") + buttonDefinition.additionalClasses
    };

    const button = $("<button>", buttonAttributes);

    if (buttonDefinition.data)
    {
        button.data(buttonDefinition.data);
    }

    if (buttonDefinition.automationID)
    {
        button.attr("data-automation-id", buttonDefinition.automationID);
    }
    else if (settings && settings.automationID && buttonDefinition.text)
    {
        button.attr("data-automation-id", `${settings.automationID}-${buttonDefinition.text.toLowerCase().replace(/ /g, "_")}-button`);
    }

    if (isCloseButton)
    {
        const buttonSvg = getSVG(buttonDefinition.icon, "MessageBox")
        if (buttonSvg)
        {
            buttonSvg.addClass("svgNoHover");
            buttonSvg.addClass("buttonSvg");
            button.append(buttonSvg);
        }
        button.attr("aria-label", "Close");
    }
    else
    {
        const buttonContent = $("<div>", { 'class': "button__fireFoxFlexContentFixForButton" });
        button.append(buttonContent);
        const buttonSvg = getSVG(buttonDefinition.icon, "MessageBox");
        if (buttonSvg)
        {
            buttonSvg.addClass("svgNoHover");
            buttonSvg.addClass("buttonSvg");
            buttonContent.append(buttonSvg);
        }

        const text = $("<span>", { 'class': buttonDefinition.icon != null ? "modalContentFooter__buttonText" : "modalContentFooter__buttonText--noIcon" }).append(buttonDefinition.text!);
        if (buttonDefinition.additionalTextClasses)
        {
            text.addClass(buttonDefinition.additionalTextClasses);
        }
        buttonContent.append(text);
    }

    setClickEvent(button, buttonDefinition.click, closeFunction, dialog);

    if (buttonDefinition.focus)
    {
        button.attr("autofocus", "true");
    }

    return button;
}

function makeDraggable(container: JQuery<HTMLElement>, options: JQueryUI.DraggableOptions)
{
    const draggableSettings = $.extend({}, getDefaultDraggableOptions(), options);

    container.draggable(
        {
            scroll: draggableSettings.scroll,
            handle: draggableSettings.handle,
            cancel: draggableSettings.cancel
        });

    setupDraggableLimits(container);

    container.find(draggableSettings.handle).css("cursor", "move");
}

function focusDialogAndSetDescription(dialog: ModalDialog)
{
    const focusedElement = setFocus(dialog.modalLayer);
    const complexHTML = "input:not([type='hidden']), textarea, select, table, ul, dl, ol";
    // We only want to add preceding content if there are no "Form Input", "Table", or "List" elements in the dialog
    if (dialog.content.find(complexHTML).length === 0)
    {
        // In case focus starts on a button or an element within the dialog, make sure the contents of the dialog
        // before the focused element get read out, by adding them to the dialog's aria-describedby.
        if (dialog.content.has(focusedElement).length === 0)
        {
            dialog.container.attr("aria-describedby", dialog.content.addIds().attr("id")!);
        }
        else
        {
            const preamble = $(getPrecedingElements(dialog.content.find(":focusable").get(0)!, dialog.content.get(0)))
                .filter(":visible:not(.fieldWrapper *, .contentMessageArea)")
                .toArray();
            dialog.container.attr("aria-describedby", preamble.map(el => $(el).addIds().attr("id")).join(" "));
        }
    }
}

function getPrecedingElements(startingElement: HTMLElement, root?: HTMLElement)
{
    if (!root || !startingElement || (startingElement.compareDocumentPosition(root) & Node.DOCUMENT_POSITION_PRECEDING) != Node.DOCUMENT_POSITION_PRECEDING)
    {
        return [];
    }
    const result = [];
    for (let currentElement = startingElement; currentElement != root; currentElement = currentElement.parentElement!)
    {
        for (; currentElement.previousElementSibling != null; currentElement = currentElement.previousElementSibling as HTMLElement)
        {
            result.push(currentElement.previousElementSibling);
        }
    }
    result.reverse();
    return result;
}

function validateAriaRole(role: string)
{
    if (!validRoles.includes(role))
    {
        console.warn(`A modal popup's ARIA role must be ${validRoles.slice(0, -1).join(", ")}, or ${validRoles[validRoles.length - 1]}, not ${role}.`);
        return "dialog";
    }
    return role;
}

function getSVG(icon?: string | JQueryInput | null, type?: IconType)
{
    if (typeof icon === 'string')
    {
        return getSvg(icon as SvgKey, type);
    }
    else if (icon && typeof icon === 'object')
    {
        return $(icon).clone();
    }
    else
    {
        return null;
    }
};

function setClickEvent(clickable: JQuery<HTMLElement>, event: ButtonDefinition["click"] | MenuItemDefinition["click"], closeFunction: (reason?: string) => any, modal?: ModalDialog | ModalMenu)
{
    if (typeof event === "function")
    {
        clickable.on("click", function (e: JQuery.ClickEvent)
        {
            if (!clickable.prop("disabled") && !clickable.hasClass("ui-disabled"))
            {
                (event as (event: JQuery.ClickEvent, modal?: ModalDialog | ModalMenu) => any).call(clickable, e, modal);
            }
        });
    }
    else if (typeof event === "string" && event.toLowerCase() !== "close")
    {
        clickable.attr("onclick", event);
    }
    else
    {
        clickable.on("click" as any, closeFunction);
    }
}

function getDefaultDraggableOptions(): JQueryUI.DraggableOptions
{
    return {
        scroll: false,
        handle: ".title"
    };
}

function getDefaultMenuSettings(): ModalMenuSettings
{
    return {
        beforeClose: null,
        afterClose: null,
        debugName: null
    };
}

function getDefaultMenuItemSettings(): MenuItemDefinition
{
    return {
        type: "item",
        id: null,
        icon: null,
        click: null,
        data: null,
        attributes: null,
        disabled: false,
        selected: false
    };
}

function getDefaultModalLayerOptions(): ModalLayerOptions
{
    return {
        titleTag: "h1",
        bindings: [],
        closeOnClick: true,
        closeOnRecordSelected: true,
        closeOnEscape: true,
        glass: "<div class='glass' >",
        initialFocusElement: undefined,
        preserveFocus: false,
        transparent: false,
        anchored: false,
        tailessAnchored: false,
        resizeCallback: null,
        debugName: null,
        role: "dialog"
    };
}

function getDefaultModalDialogSettings(): ModalDialogSettings
{
    return {
        titleTag: "h1",
        allowNoButtons: false,
        useMobileAccessStyles: false,
        anchorTo: null,
        afterClose: null,
        beforeClose: null,
        closeOnClick: true,
        closeOnEscape: true,
        draggable: false,
        id: null,
        transparentGlass: false,
        forceClose: false,
        width: null,
        animationDuration: 100,
        beforeShowContentPromise: null,
        dynamicDialogContentSelector: undefined,
        additionalClasses: "",
        closeOnRecordSelected: true,
        automationID: undefined,
        role: "dialog"
    };
}
